/**
 * Archived Konva-based table planner (v1). Kept for emergency rollback; prefer VisualTablePlannerPage (Pixi).
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, setDoc, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Stage, Layer, Rect, Text, Shape } from 'react-konva';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import {
  Plus,
  Save,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Upload,
  FolderOpen,
  Download,
  Maximize2,
  Copy,
  Building2,
  Users,
} from 'lucide-react';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import Swal from 'sweetalert2';
import { RSVP } from '@/types';
import { useAuth } from '@masonic-tools/shared-auth';
import VisualTable from '@/components/table-planner/VisualTable';
import TableConfigModal, { TableConfig } from '@/components/table-planner/TableConfigModal';
import GuestAssignmentModal from '@/components/table-planner/GuestAssignmentModal';
import PDFExportModal from '@/components/table-planner/PDFExportModal';
import AutoPopulateModal from '@/components/table-planner/AutoPopulateModal';
import Konva from 'konva';
import jsPDF from 'jspdf';
import { useRanks } from '@/hooks/useRanks';
import { formatGuestListDisplayName } from '@/lib/rank-utils';


// Helper function to remove undefined values from objects (Firestore doesn't support undefined)
function sanitizeForFirestore<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as any;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirestore(item)) as any;
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned;
  }
  
  return obj;
}

const TABLE_PLANNER_PLAYABLE_AREA_SIZE = 1000;

const devLog =
  import.meta.env.DEV ? (...args: unknown[]) => { console.log(...args); } : () => {};

export default function VisualTablePlannerV1Page() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { ranks } = useRanks();
  
  // Helper to check if user is admin for detailed logging
  const isAdmin = userProfile?.isAdmin === true;
  
  // Admin-only detailed logging helper
  const adminLog = (...args: any[]) => {
    if (isAdmin) {
      console.log('[ADMIN DEBUG - Table Planner]', ...args);
    }
  };
  
  const adminWarn = (...args: any[]) => {
    if (isAdmin) {
      console.warn('[ADMIN DEBUG - Table Planner]', ...args);
    }
  };
  
  const adminError = (...args: any[]) => {
    if (isAdmin) {
      console.error('[ADMIN DEBUG - Table Planner]', ...args);
    }
  };
  const stageRef = useRef<Konva.Stage>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Canvas state
  const [stageWidth, setStageWidth] = useState(window.innerWidth - 400);
  const [stageHeight, setStageHeight] = useState(window.innerHeight - 200);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

  // Tables and guests
  const [tables, setTables] = useState<TableConfig[]>([]);
  const tablesRef = useRef<TableConfig[]>(tables);
  const scaleRef = useRef(scale);
  const stagePosRef = useRef(stagePos);
  tablesRef.current = tables;
  scaleRef.current = scale;
  stagePosRef.current = stagePos;

  const viewportAutoSaveTimeoutRef = useRef<number | null>(null);
  /** True while the user is actively panning the canvas (Stage drag). Used to suppress deferred saves mid-gesture. */
  const isViewportPanningRef = useRef(false);

  const [allGuests, setAllGuests] = useState<RSVP[]>([]);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [showSidebar, setShowSidebar] = useState(true);
  const [isDraggingTable, setIsDraggingTable] = useState(false);
  const [dragStartPositions, setDragStartPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const dragMoveThrottleRef = useRef<{
    rafId: number | null;
    lastDelta: { deltaX: number; deltaY: number } | null;
  }>({ rafId: null, lastDelta: null });
  const [alignmentGuides, setAlignmentGuides] = useState<
    { type: 'vertical' | 'horizontal'; position: number }[]
  >([]);
  const [clipboardTable, setClipboardTable] = useState<TableConfig | null>(null);
  const selectedTableIdsRef = useRef(selectedTableIds);
  const clipboardTableRef = useRef(clipboardTable);
  const dragStartPositionsRef = useRef(dragStartPositions);
  selectedTableIdsRef.current = selectedTableIds;
  clipboardTableRef.current = clipboardTable;
  dragStartPositionsRef.current = dragStartPositions;

  const tableGroupRefs = useRef<Map<string, Konva.Group>>(new Map());
  const registerTableGroup = useCallback((id: string, node: Konva.Group | null) => {
    if (node) {
      tableGroupRefs.current.set(id, node);
    } else {
      tableGroupRefs.current.delete(id);
    }
  }, []);

  const [draggedGuest, setDraggedGuest] = useState<RSVP | null>(null);
  const [draggedSeat, setDraggedSeat] = useState<{ tableId: string; seatNumber: number; guestId: string } | null>(null);
  const [isDraggingSeat, setIsDraggingSeat] = useState(false);
  const [dragPreview, setDragPreview] = useState<{ x: number; y: number; guestName: string } | null>(null);
  const [isOverUnassignZone, setIsOverUnassignZone] = useState(false);
  const dragPreviewRafRef = useRef<number | null>(null);
  const pendingDragPreviewRef = useRef<{ x: number; y: number; guestName: string } | null>(null);
  const lastUnassignZoneRef = useRef(false);
  const [highlightedRelatedGuests, setHighlightedRelatedGuests] = useState<Set<string>>(new Set());
  const justDroppedRef = useRef<{ tableId: string; seatNumber: number; timestamp: number } | null>(null);
  const updateUnassignZone = useCallback((nextValue: boolean) => {
    if (lastUnassignZoneRef.current !== nextValue) {
      lastUnassignZoneRef.current = nextValue;
      setIsOverUnassignZone(nextValue);
    }
  }, []);
  const queueDragPreviewUpdate = useCallback((preview: { x: number; y: number; guestName: string }) => {
    pendingDragPreviewRef.current = preview;
    if (dragPreviewRafRef.current === null) {
      dragPreviewRafRef.current = requestAnimationFrame(() => {
        dragPreviewRafRef.current = null;
        if (pendingDragPreviewRef.current) {
          setDragPreview(pendingDragPreviewRef.current);
        }
      });
    }
  }, []);
  const resetSeatDragState = useCallback(() => {
    if (dragPreviewRafRef.current !== null) {
      cancelAnimationFrame(dragPreviewRafRef.current);
      dragPreviewRafRef.current = null;
    }
    pendingDragPreviewRef.current = null;
    setDraggedSeat(null);
    setIsDraggingSeat(false);
    setDragPreview(null);
    lastUnassignZoneRef.current = false;
    setIsOverUnassignZone(false);
    setHighlightedRelatedGuests(new Set());
  }, []);

  // Modals
  const [configModalTable, setConfigModalTable] = useState<TableConfig | null>(null);
  const [guestAssignmentModal, setGuestAssignmentModal] = useState<{
    tableId: string;
    tableName: string;
    seatNumber: number;
    currentGuest: RSVP | null;
  } | null>(null);
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const [showAutoPopulateModal, setShowAutoPopulateModal] = useState(false);
  
  // Room state
  const [rooms, setRooms] = useState<{ id: string; name: string }[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [allRoomTables, setAllRoomTables] = useState<Map<string, TableConfig[]>>(new Map());

  // Grid settings
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const gridSize = 100; // 1 meter = 100cm

  const guestSeatAssignments = useMemo(() => {
    const assignments = new Map<string, { tableName: string; seatNumber: number }>();

    tables.forEach((table) => {
      table.seating.forEach((seat) => {
        if (seat.rsvpId !== undefined && seat.rsvpId !== null) {
          assignments.set(seat.rsvpId, {
            tableName: table.name,
            seatNumber: seat.seatNumber,
          });
        }
      });
    });

    return assignments;
  }, [tables]);

  const sortedGuests = useMemo(() => {
    const guests = [...allGuests];
    guests.sort((a, b) => {
      const seatInfoA = a.id ? guestSeatAssignments.get(a.id) : undefined;
      const seatInfoB = b.id ? guestSeatAssignments.get(b.id) : undefined;

      const seatNumberA = seatInfoA?.seatNumber;
      const seatNumberB = seatInfoB?.seatNumber;

      if (seatNumberA === undefined && seatNumberB === undefined) {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }

      if (seatNumberA === undefined) {
        return 1;
      }

      if (seatNumberB === undefined) {
        return -1;
      }

      if (seatNumberA === seatNumberB) {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }

      return seatNumberB - seatNumberA;
    });
    return guests;
  }, [allGuests, guestSeatAssignments]);

  const guestListScrollRef = useRef<HTMLDivElement>(null);
  const guestRowVirtualizer = useVirtualizer({
    count: sortedGuests.length,
    getScrollElement: () => guestListScrollRef.current,
    estimateSize: () => 92,
    overscan: 8,
  });

  const gridLayer = useMemo(() => {
    if (!showGrid) return null;
    const w = TABLE_PLANNER_PLAYABLE_AREA_SIZE;
    const h = TABLE_PLANNER_PLAYABLE_AREA_SIZE;
    const gs = gridSize;
    return (
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.strokeStyle = '#e5e7eb';
          ctx.lineWidth = 1 / scale;
          for (let i = 0; i <= w / gs; i++) {
            ctx.beginPath();
            ctx.moveTo(i * gs, 0);
            ctx.lineTo(i * gs, h);
            ctx.stroke();
          }
          for (let i = 0; i <= h / gs; i++) {
            ctx.beginPath();
            ctx.moveTo(0, i * gs);
            ctx.lineTo(w, i * gs);
            ctx.stroke();
          }
          ctx.fillStrokeShape(shape);
        }}
        listening={false}
      />
    );
  }, [showGrid, gridSize, scale]);

  useEffect(() => {
    const handleResize = () => {
      setStageWidth(window.innerWidth - (showSidebar ? 400 : 100));
      setStageHeight(window.innerHeight - 200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showSidebar]);

  useEffect(() => {
    return () => {
      if (dragPreviewRafRef.current !== null) {
        cancelAnimationFrame(dragPreviewRafRef.current);
      }
    };
  }, []);

  // Lock document to viewport so there is no page scroll (only guest list scrolls)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtml = { height: html.style.height, overflow: html.style.overflow };
    const prevBody = { height: body.style.height, overflow: body.style.overflow, margin: body.style.margin };
    const prevRoot = root ? { height: (root as HTMLElement).style.height, overflow: (root as HTMLElement).style.overflow } : null;
    html.style.height = '100%';
    html.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.margin = '0';
    if (root) {
      (root as HTMLElement).style.height = '100%';
      (root as HTMLElement).style.overflow = 'hidden';
    }
    return () => {
      html.style.height = prevHtml.height;
      html.style.overflow = prevHtml.overflow;
      body.style.height = prevBody.height;
      body.style.overflow = prevBody.overflow;
      body.style.margin = prevBody.margin;
      if (root && prevRoot) {
        (root as HTMLElement).style.height = prevRoot.height;
        (root as HTMLElement).style.overflow = prevRoot.overflow;
      }
    };
  }, []);

  // Fetch guests data
  useEffect(() => {
    const fetchGuests = async () => {
      if (!unitId || !eventId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch RSVPs
        const rsvpsSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'rsvps')
        );

        const rsvps: RSVP[] = [];

        rsvpsSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const isApology = data.apologies === true;

          if (isApology) {
            return;
          }
          
          rsvps.push({
            id: docSnap.id,
            eventId,
            userId: data.userId,
            name: data.name,
            rank: data.rank,
            rankShortName: data.rankShortName,
            unit: data.unit,
            email: data.email,
            tag: data.tag || null,
            responses: data.responses,
            status: data.status,
            invoiceId: data.invoiceId,
            paid: data.paid,
            invitedBy: data.invitedBy,
            checkedIn: data.checkedIn,
            checkInTime: data.checkInTime?.toDate(),
            specialRequests: data.specialRequests,
            plusOneAllowed: data.plusOneAllowed,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            apologies: data.apologies === true,
          });
        });

        setAllGuests(rsvps);
      } catch (error) {
        console.error('Error fetching guests:', error);
        showError('Failed to load guests');
      } finally {
        setLoading(false);
      }
    };

    fetchGuests();
  }, [unitId, eventId]);

  // Room functions
  const fetchRooms = async () => {
    if (!unitId || !eventId) return;
    
    try {
      const roomsSnapshot = await getDocs(
        collection(db, 'units', unitId, 'events', eventId, 'rooms')
      );
      
      const roomsList: { id: string; name: string }[] = [];
      roomsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        roomsList.push({
          id: docSnap.id,
          name: data.name
        });
      });
      
      setRooms(roomsList);
      
      // If no rooms exist, show create room modal
      if (roomsList.length === 0) {
        setShowCreateRoomModal(true);
      } else if (!selectedRoomId) {
        // Select first room by default
        setSelectedRoomId(roomsList[0].id);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      showError('Failed to load rooms');
    }
  };

  const createRoom = async () => {
    if (!newRoomName.trim() || !unitId || !eventId) return;
    
    try {
      const roomData = {
        name: newRoomName.trim(),
        createdAt: new Date().toISOString()
      };
      
      // Sanitize data to remove undefined values
      const sanitizedRoomData = sanitizeForFirestore(roomData);
      
      const roomRef = await addDoc(
        collection(db, 'units', unitId, 'events', eventId, 'rooms'),
        sanitizedRoomData
      );
      
      const newRoom = { id: roomRef.id, name: newRoomName.trim() };
      setRooms(prev => [...prev, newRoom]);
      setSelectedRoomId(roomRef.id);
      setNewRoomName('');
      setShowCreateRoomModal(false);
      
      showSuccess('Room created successfully');
    } catch (error) {
      console.error('Error creating room:', error);
      showError('Failed to create room');
    }
  };

  const loadRoomTablePlan = async (roomId: string) => {
    if (!unitId || !eventId) return;
    
    try {
      adminLog('Loading table plan for room:', roomId);
      
      const tablePlanDoc = await getDoc(
        doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId)
      );
      
      if (tablePlanDoc.exists()) {
        const data = tablePlanDoc.data();
        console.log('Table plan data loaded:', data);
        
        // Migrate existing tables to include seatPlacement, chairColor, and seatNumberPriority if missing
        const migratedTables = (data.tables || []).map((table: any, index: number) => {
          const isSquare = table.type === 'square';
          const defaultSeatPlacement = isSquare ? {
            bothSides: false,
            oneSide: false,
            leftEnd: false,
            rightEnd: false,
            noSeats: false,
            topSide: true,
            bottomSide: true,
            leftSide: true,
            rightSide: true,
          } : {
            bothSides: true,
            oneSide: false,
            leftEnd: false,
            rightEnd: false,
            noSeats: false,
            // New explicit rectangular side flags (default both on)
            topSide: table?.seatPlacement?.topSide ?? true,
            bottomSide: table?.seatPlacement?.bottomSide ?? true,
          };
          
          return {
            ...table,
            seatPlacement: (() => {
              const sp = table.seatPlacement || defaultSeatPlacement;
              // Ensure top/bottom flags exist for rectangular tables
              if (table.type === 'rectangular') {
                const both = sp.bothSides === true;
                const one = sp.oneSide === true;
                const top = sp.topSide ?? (both || one);
                const bottom = sp.bottomSide ?? (both || (!one && both) || (!one && !both ? false : both));
                return { ...sp, topSide: top, bottomSide: bottom };
              }
              return sp;
            })(),
            seatCounts: table.seatCounts || undefined,
            chairColor: table.chairColor || '#e5e7eb',
            seatNumberPriority: table.seatNumberPriority || (index + 1)
          };
        });
        const recalculatedTables = recalculateSeatNumbers(migratedTables);
        console.log('Migrated and recalculated tables:', recalculatedTables);
        setTables(recalculatedTables);
        
        if (data.viewport) {
          console.log('Restoring viewport:', data.viewport);
          setScale(data.viewport.scale || 1);
          setStagePos(data.viewport.position || { x: 0, y: 0 });
        }
      } else {
        console.log('No table plan found for room:', roomId);
        // No table plan exists for this room, start with empty tables
        setTables([]);
        setScale(1);
        setStagePos({ x: 0, y: 0 });
      }
    } catch (error) {
      console.error('Error loading room table plan:', error);
      showError('Failed to load room table plan');
    }
  };

  const loadAllRoomTablePlans = async () => {
    if (!unitId || !eventId) return;
    
    try {
      const allTables = new Map<string, TableConfig[]>();
      
      for (const room of rooms) {
        const tablePlanRef = doc(db, 'units', unitId, 'events', eventId, 'tablePlans', room.id);
        const tablePlanSnap = await getDoc(tablePlanRef);
        
        if (tablePlanSnap.exists()) {
          const data = tablePlanSnap.data();
          const tables = data.tables || [];
          allTables.set(room.id, tables);
        } else {
          allTables.set(room.id, []);
        }
      }
      
      setAllRoomTables(allTables);
    } catch (error) {
      console.error('Error loading all room table plans:', error);
    }
  };

  const autoSaveTablePlan = async (tablesToSave: TableConfig[]) => {
    if (!unitId || !eventId || !selectedRoomId) {
      adminLog('Auto-save skipped - missing params:', { unitId, eventId, selectedRoomId });
      return;
    }
    
    try {
      setAutoSaving(true);
      
      const tablePlanData = {
        tables: tablesToSave,
        viewport: {
          scale: scaleRef.current,
          position: stagePosRef.current,
        },
        lastUpdated: new Date().toISOString()
      };
      
      // Sanitize data to remove undefined values (Firestore doesn't support undefined)
      const sanitizedData = sanitizeForFirestore(tablePlanData);
      
      adminLog(
        'Auto-save',
        tablesToSave.length,
        'tables →',
        `tablePlans/${selectedRoomId}`,
        `(viewport ${scaleRef.current.toFixed(2)}x @ ${Math.round(stagePosRef.current.x)},${Math.round(stagePosRef.current.y)})`
      );
      
      // Create a timeout promise to detect hanging operations
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Save operation timed out after 10 seconds')), 10000);
      });
      
      // Race between the save operation and timeout
      const savePromise = Promise.all([
        // Save to room-specific location
        setDoc(
          doc(db, 'units', unitId, 'events', eventId, 'tablePlans', selectedRoomId),
          sanitizedData,
          { merge: true }
        ).then(() => {
          /* room tablePlans doc */
        }).catch((error) => {
          console.error('❌ Failed to save to room-specific location:', error);
          throw error;
        }),
        // ALSO save to visualLayout for place card designer and other consumers
        setDoc(
          doc(db, 'units', unitId, 'events', eventId, 'tablePlan', 'visualLayout'),
          sanitizedData,
          { merge: true }
        ).then(() => {
          return getDoc(doc(db, 'units', unitId, 'events', eventId, 'tablePlan', 'visualLayout'));
        }).then((docSnap) => {
          if (docSnap.exists()) {
            const savedData = docSnap.data();
            if ((savedData.tables?.length ?? 0) !== tablesToSave.length) {
              adminWarn('visualLayout verify: table count mismatch after save');
            }
          } else {
            adminWarn('visualLayout save verification failed - document missing after save');
          }
        }).catch((error) => {
          adminError('❌ Failed to save to visualLayout:', error);
          adminError('Error details:', {
            code: error?.code,
            message: error?.message,
            stack: error?.stack,
          });
          throw error;
        }),
        // Also save legacy format for compatibility
        setDoc(
          doc(db, 'units', unitId, 'events', eventId, 'tablePlan', 'layout'),
          sanitizeForFirestore({
            tables: tablesToSave.map((t) => ({
              id: t.id,
              type: t.type,
              seats: t.seats,
              name: t.name,
              seating: t.seating.map((s) => ({
                seatNumber: s.seatNumber,
                rsvpId: s.rsvpId,
                role: s.role,
              })),
            })),
            lastUpdated: new Date().toISOString(),
          }),
          { merge: true }
        ).then(() => {
          /* legacy layout doc */
        }).catch((error) => {
          console.error('❌ Failed to save to legacy layout format:', error);
          throw error;
        }),
      ]);
      
      await Promise.race([savePromise, timeoutPromise]);
      
      adminLog('Auto-save OK');
      setLastSaved(new Date());
    } catch (error: any) {
      console.error('Error auto-saving table plan:', error);
      console.error('Error code:', error?.code);
      console.error('Error message:', error?.message);
      
      // Show error to user
      if (error?.message?.includes('timeout')) {
        console.error('⚠️ Firebase connection may be down. Are the emulators running?');
        showError('Auto-save timed out. Check your connection and ensure Firebase emulators are running.');
      } else if (error?.message?.includes('undefined')) {
        console.error('⚠️ Data contains undefined values.');
        showError('Failed to auto-save: Invalid data format');
      } else {
        showError(`Failed to auto-save: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setAutoSaving(false);
    }
  };

  const cancelPendingViewportAutoSave = () => {
    if (viewportAutoSaveTimeoutRef.current !== null) {
      window.clearTimeout(viewportAutoSaveTimeoutRef.current);
      viewportAutoSaveTimeoutRef.current = null;
    }
  };

  /**
   * Debounced save after zoom / wheel / toolbar view actions.
   * Stage pan does not use this until the pointer is released (see Stage onDragEnd).
   */
  const scheduleViewportAutoSave = (delayMs = 1200) => {
    if (!selectedRoomId || loading) return;
    cancelPendingViewportAutoSave();
    viewportAutoSaveTimeoutRef.current = window.setTimeout(() => {
      viewportAutoSaveTimeoutRef.current = null;
      if (isViewportPanningRef.current) {
        return;
      }
      void autoSaveTablePlan(tablesRef.current);
    }, delayMs);
  };

  // Test Firebase connection on mount
  useEffect(() => {
    const testFirebaseConnection = async () => {
      try {
        console.log('🔍 Testing Firebase connection...');
        console.log('Firebase config:', { unitId, eventId });
        console.log('Firestore instance:', db);
        
        // Try a simple read operation to test connectivity
        if (unitId && eventId) {
          const testDoc = await getDoc(doc(db, 'units', unitId));
          console.log('✅ Firebase connection successful', testDoc.exists() ? '(document exists)' : '(document does not exist)');
        }
      } catch (error: any) {
        console.error('❌ Firebase connection test failed:', error);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        
        if (error?.code === 'unavailable') {
          console.error('⚠️ Firebase Firestore is unavailable. Make sure emulators are running:');
          console.error('   Run: firebase emulators:start');
        }
      }
    };
    
    testFirebaseConnection();
  }, []);

  // Load rooms on component mount
  useEffect(() => {
    if (unitId && eventId) {
      fetchRooms();
    }
  }, [unitId, eventId]);

  // Load all room table plans when rooms change
  useEffect(() => {
    if (rooms.length > 0) {
      loadAllRoomTablePlans();
    }
  }, [rooms, unitId, eventId]);

  // Load table plan when room changes
  useEffect(() => {
    if (selectedRoomId) {
      loadRoomTablePlan(selectedRoomId);
    }
  }, [selectedRoomId]);

  // Save current room's layout before switching rooms
  const saveCurrentRoomLayout = async () => {
    if (selectedRoomId && tables.length > 0) {
      console.log('Saving current room layout before switching');
      await autoSaveTablePlan(tables);
    }
  };

  // Handle room selection with auto-save
  const handleRoomChange = async (newRoomId: string) => {
    if (selectedRoomId && selectedRoomId !== newRoomId) {
      await saveCurrentRoomLayout();
    }
    setSelectedRoomId(newRoomId);
  };

  // Update allRoomTables when current room's tables change (skip during drag to reduce work)
  const isDraggingTableRef = useRef(false);
  isDraggingTableRef.current = isDraggingTable;
  useEffect(() => {
    if (selectedRoomId && !loading && !isDraggingTableRef.current) {
      setAllRoomTables((prev) => {
        const updated = new Map(prev);
        updated.set(selectedRoomId, tables);
        return updated;
      });
    }
  }, [tables, selectedRoomId, loading, isDraggingTable]);

  // Auto-save when tables change (not on every pan/zoom frame; viewport uses scheduleViewportAutoSave)
  useEffect(() => {
    if (selectedRoomId && !loading) {
      const timeoutId = window.setTimeout(() => {
        void autoSaveTablePlan(tables);
      }, 1000);

      return () => window.clearTimeout(timeoutId);
    }
  }, [tables, selectedRoomId, loading]);

  useEffect(() => {
    return () => {
      cancelPendingViewportAutoSave();
    };
  }, []);

  const handleAddTable = (tableType: 'rectangular' | 'square' | 'circular' | 'oval' = 'rectangular') => {
    const newTable: TableConfig = {
      id: `table-${Date.now()}`,
      name: `Table ${tables.length + 1}`,
      type: tableType,
      seats: 8,
      x: TABLE_PLANNER_PLAYABLE_AREA_SIZE / 2,
      y: TABLE_PLANNER_PLAYABLE_AREA_SIZE / 2,
      rotation: 0,
      width: 150,
      height: tableType === 'rectangular' ? 80 : 150, // 0.8m height for rectangular, square for others
      color: '#ffffff',
      seatPlacement: {
        bothSides: tableType === 'rectangular' ? true : false,
        oneSide: false,
        leftEnd: false,
        rightEnd: false,
        noSeats: false,
        // Square table defaults - all sides enabled
        topSide: tableType === 'square' ? true : undefined,
        bottomSide: tableType === 'square' ? true : undefined,
        leftSide: tableType === 'square' ? true : undefined,
        rightSide: tableType === 'square' ? true : undefined,
      },
      chairColor: '#e5e7eb',
      seatSize: 28,
      seatNumberPriority: tables.length + 1, // Default priority based on table order
      seating: Array.from({ length: 8 }, (_, i) => ({
        seatNumber: i + 1,
        rsvpId: null,
        role: null,
        x: 0,
        y: 0,
      })),
    };

    const calculatedTable = calculateSeatingPositions(newTable);
    const updatedTables = [...tables, calculatedTable];
    const recalculatedTables = recalculateSeatNumbers(updatedTables);
    setTables(recalculatedTables);
  };

  const handleTableDragStart = useCallback((tableId: string) => {
    if (isDraggingSeat) return;
    setIsDraggingTable(true);
    
    // Store starting positions of all selected tables (or just the dragged one if not selected)
    const tablesToMove = selectedTableIds.has(tableId) ? Array.from(selectedTableIds) : [tableId];
    const startPositions = new Map<string, { x: number; y: number }>();
    
    tablesToMove.forEach(id => {
      const table = tables.find(t => t.id === id);
      if (table) {
        startPositions.set(id, { x: table.x, y: table.y });
      }
    });
    
    setDragStartPositions(startPositions);
    
    // If dragging a table that's not selected, select only that table
    if (!selectedTableIds.has(tableId)) {
      setSelectedTableIds(new Set([tableId]));
    }
  }, [isDraggingSeat, selectedTableIds, tables]);

  const handleTableDragMove = useCallback((tableId: string, x: number, y: number) => {
    if (isDraggingSeat) return;
    const starts = dragStartPositionsRef.current;
    const startPos = starts.get(tableId);
    if (!startPos) return;

    const deltaX = x - startPos.x;
    const deltaY = y - startPos.y;

    dragMoveThrottleRef.current.lastDelta = { deltaX, deltaY };
    const { rafId } = dragMoveThrottleRef.current;

    if (rafId === null) {
      dragMoveThrottleRef.current.rafId = requestAnimationFrame(() => {
        dragMoveThrottleRef.current.rafId = null;
        const pending = dragMoveThrottleRef.current.lastDelta;
        if (!pending) return;
        const map = dragStartPositionsRef.current;
        const sel = selectedTableIdsRef.current;
        const toMove = sel.has(tableId) ? Array.from(sel) : [tableId];
        toMove.forEach((id) => {
          const tableStartPos = map.get(id);
          const node = tableGroupRefs.current.get(id);
          if (tableStartPos && node) {
            node.x(tableStartPos.x + pending.deltaX);
            node.y(tableStartPos.y + pending.deltaY);
          }
        });
        tableGroupRefs.current.get(tableId)?.getLayer()?.batchDraw();
      });
    }
  }, [isDraggingSeat]);

  const handleTableDragEnd = useCallback((tableId: string, x: number, y: number) => {
    if (isDraggingSeat) return;
    const { rafId, lastDelta } = dragMoveThrottleRef.current;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      dragMoveThrottleRef.current.rafId = null;
    }
    const startPositions = new Map(dragStartPositions);
    setIsDraggingTable(false);
    setDragStartPositions(new Map());
    setAlignmentGuides([]);

    const tablesToMove = selectedTableIds.has(tableId) ? Array.from(selectedTableIds) : [tableId];
    const primaryStart = startPositions.get(tableId);

    setTables((prevTables) => {
      let next = prevTables;
      const deltaX = primaryStart != null ? x - primaryStart.x : 0;
      const deltaY = primaryStart != null ? y - primaryStart.y : 0;
      next = prevTables.map((t) => {
        if (!tablesToMove.includes(t.id)) return t;
        const tableStart = startPositions.get(t.id);
        if (tableStart != null) {
          const newX = t.id === tableId ? x : tableStart.x + deltaX;
          const newY = t.id === tableId ? y : tableStart.y + deltaY;
          return { ...t, x: newX, y: newY };
        }
        return t;
      });
      if (snapToGrid) {
        next = next.map((t) => {
          if (tablesToMove.includes(t.id)) {
            const snapIncrement = 10;
            const snappedX = Math.round(t.x / snapIncrement) * snapIncrement;
            const snappedY = Math.round(t.y / snapIncrement) * snapIncrement;
            return { ...t, x: snappedX, y: snappedY };
          }
          return t;
        });
      }
      return next;
    });
  }, [isDraggingSeat, selectedTableIds, snapToGrid, dragStartPositions]);

  const handleTableSelect = useCallback((tableId: string, event?: MouseEvent | React.MouseEvent) => {
    const isMultiSelect = event && (event.ctrlKey || event.metaKey);
    
    if (isMultiSelect) {
      // Toggle selection: add if not selected, remove if selected
      setSelectedTableIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(tableId)) {
          newSet.delete(tableId);
        } else {
          newSet.add(tableId);
        }
        return newSet;
      });
    } else {
      // Single select: replace selection
      setSelectedTableIds(new Set([tableId]));
    }
    
    // Bring selected table to front by moving it to end of array
    setTables((prevTables) => {
      const filtered = prevTables.filter((t) => t.id !== tableId);
      return [...filtered, prevTables.find((t) => t.id === tableId)!];
    });
  }, []);

  const handleTableDoubleClick = useCallback((tableId: string) => {
    // Double-click opens configuration modal
    const table = tables.find((t) => t.id === tableId);
    if (table) {
      setConfigModalTable(table);
    }
  }, [tables]);

  const handleSaveTableConfig = (updatedTable: TableConfig) => {
    const updatedTables = tables.map((t) => (t.id === updatedTable.id ? updatedTable : t));
    const recalculatedTables = recalculateSeatNumbers(updatedTables);
    setTables(recalculatedTables);
    setConfigModalTable(null);
  };

  const handleUpdateTableConfig = (updatedTable: TableConfig) => {
    const updatedTables = tables.map((t) => (t.id === updatedTable.id ? updatedTable : t));
    const recalculatedTables = recalculateSeatNumbers(updatedTables);
    setTables(recalculatedTables);
  };

  const handleTableResize = useCallback((tableId: string, width: number, height: number, x: number, y: number) => {
    setTables((prevTables) =>
      prevTables.map((t) => {
        if (t.id === tableId) {
          const updatedTable = { ...t, width, height, x, y };
          // Recalculate seating positions when table is resized
          return calculateSeatingPositions(updatedTable);
        }
        return t;
      })
    );
  }, []);

  const handleSeatsChange = useCallback((tableId: string, newSeats: number) => {
    setTables((prevTables) => {
      const updatedTables = prevTables.map((t) => {
        if (t.id === tableId) {
        const oldTable = t;
          const updatedTable = { ...t, seats: newSeats };
          // Recalculate seating positions when seat count changes
        const newTable = calculateSeatingPositions(updatedTable);
        // Preserve guests on their side
        return preserveGuestsBySide(oldTable, newTable);
        }
        return t;
      });
      // Recalculate seat numbers across all tables to ensure no duplicates or gaps
      return recalculateSeatNumbers(updatedTables);
    });
  }, []);

  const adjustSideSeatCount = (
    tableId: string,
    side: 'top' | 'bottom' | 'left' | 'right' | 'leftEnd' | 'rightEnd',
    delta: number
  ) => {
    const deriveSeatCounts = (table: TableConfig) => {
      const counts: any = { top: 0, bottom: 0, left: 0, right: 0, leftEnd: 0, rightEnd: 0 };
      const width = table.width;
      const height = table.height;
      table.seating.forEach((s) => {
        // Ends: chairs positioned beyond table width horizontally
        if (s.x <= -width / 2 - 20) counts.leftEnd++;
        else if (s.x >= width / 2 + 20) counts.rightEnd++;
        // Sides for rectangular/square: y sign indicates top/bottom; x sign for left/right on square
        else if (s.y < 0) counts.top++;
        else if (s.y > 0) counts.bottom++;
        else if (s.x < 0) counts.left++;
        else if (s.x > 0) counts.right++;
      });
      return counts;
    };

    const updatedTables = tables.map((t) => {
        if (t.id !== tableId) return t;
      
      const oldTable = t;
        
        // For circular tables, just adjust the total seat count
        if (t.type === 'circular') {
          const newSeats = Math.max(0, t.seats + delta);
        const newTable = calculateSeatingPositions({ ...t, seats: newSeats });
        // Preserve guests on their side (by angle for circular)
        return preserveGuestsBySide(oldTable, newTable);
        }
        
        // For rectangular/square tables, adjust per-side counts
        const currentCounts = { ...deriveSeatCounts(t), ...(t as any).seatCounts } as any;
        let nextCounts = { ...currentCounts };
        const current = currentCounts[side] ?? 0;
        let next = current + delta;
        next = Math.max(0, Math.floor(next));
        nextCounts[side] = next;
        const total = t.type === 'rectangular'
          ? (nextCounts.top || 0) + (nextCounts.bottom || 0) + (nextCounts.leftEnd || 0) + (nextCounts.rightEnd || 0)
          : (nextCounts.top || 0) + (nextCounts.bottom || 0) + (nextCounts.left || 0) + (nextCounts.right || 0);
      const newTable = calculateSeatingPositions({ ...t, seatCounts: nextCounts, seats: total });
      // Preserve guests on their side
      return preserveGuestsBySide(oldTable, newTable);
    });
    
    // Recalculate seat numbers across all tables to ensure no duplicates or gaps
    const recalculatedTables = recalculateSeatNumbers(updatedTables);
    setTables(recalculatedTables);
  };

  const handleTableRotate = useCallback((tableId: string, rotation: number) => {
    setTables((prevTables) =>
      prevTables.map((t) => (t.id === tableId ? { ...t, rotation } : t))
    );
  }, []);

  const handleToggleSeatSide = useCallback((tableId: string, side: 'top' | 'bottom' | 'left' | 'right') => {
    let changedTable: TableConfig | null = null;
    const updatedTables = tables.map((t) => {
      if (t.id !== tableId) return t;

      // Get current placement with defaults
      const currentPlacement = t.seatPlacement || {
        bothSides: true,
        oneSide: false,
        leftEnd: false,
        rightEnd: false,
        noSeats: false,
      };
      
      let updatedSeatPlacement: typeof t.seatPlacement;

      if (t.type === 'rectangular') {
        // Derive current top/bottom from explicit flags first, then fall back to legacy fields
        const currentTop = (currentPlacement as any).topSide ?? (currentPlacement.bothSides === true || (currentPlacement.bothSides === false && currentPlacement.oneSide === true));
        const currentBottom = (currentPlacement as any).bottomSide ?? (currentPlacement.bothSides === true || (currentPlacement.bothSides === false && currentPlacement.oneSide === false));

        if (side === 'top' || side === 'bottom') {
          const nextTop = side === 'top' ? !currentTop : currentTop;
          const nextBottom = side === 'bottom' ? !currentBottom : currentBottom;

          if (nextTop && nextBottom) {
            updatedSeatPlacement = {
              bothSides: true,
              oneSide: false,
              leftEnd: currentPlacement.leftEnd,
              rightEnd: currentPlacement.rightEnd,
              noSeats: false,
              topSide: true,
              bottomSide: true,
            };
          } else if (nextTop && !nextBottom) {
            updatedSeatPlacement = {
              bothSides: false,
              oneSide: true, // top only
              leftEnd: currentPlacement.leftEnd,
              rightEnd: currentPlacement.rightEnd,
              noSeats: false,
              topSide: true,
              bottomSide: false,
            };
          } else if (!nextTop && nextBottom) {
            updatedSeatPlacement = {
              bothSides: false,
              oneSide: false, // bottom only
              leftEnd: currentPlacement.leftEnd,
              rightEnd: currentPlacement.rightEnd,
              noSeats: false,
              topSide: false,
              bottomSide: true,
            };
          } else { // neither side enabled
            updatedSeatPlacement = {
              bothSides: false,
              oneSide: false,
              leftEnd: currentPlacement.leftEnd,
              rightEnd: currentPlacement.rightEnd,
              noSeats: true,
              topSide: false,
              bottomSide: false,
            };
          }
        } else if (side === 'left') {
          // Toggle left end seat
          updatedSeatPlacement = {
            ...currentPlacement,
            leftEnd: !currentPlacement.leftEnd,
            noSeats: false,
          };
        } else if (side === 'right') {
          // Toggle right end seat
          updatedSeatPlacement = {
            ...currentPlacement,
            rightEnd: !currentPlacement.rightEnd,
            noSeats: false,
          };
        } else {
          updatedSeatPlacement = currentPlacement;
        }
      } else if (t.type === 'square') {
        // For square tables, toggle individual sides
        updatedSeatPlacement = {
          bothSides: currentPlacement.bothSides,
          oneSide: currentPlacement.oneSide,
          leftEnd: currentPlacement.leftEnd,
          rightEnd: currentPlacement.rightEnd,
          noSeats: false,
          topSide: side === 'top' ? !(currentPlacement.topSide !== false) : (currentPlacement.topSide ?? true),
          bottomSide: side === 'bottom' ? !(currentPlacement.bottomSide !== false) : (currentPlacement.bottomSide ?? true),
          leftSide: side === 'left' ? !(currentPlacement.leftSide !== false) : (currentPlacement.leftSide ?? true),
          rightSide: side === 'right' ? !(currentPlacement.rightSide !== false) : (currentPlacement.rightSide ?? true),
        };
      } else {
        updatedSeatPlacement = currentPlacement;
      }

      const updatedTable = calculateSeatingPositions({ ...t, seatPlacement: updatedSeatPlacement });
      changedTable = updatedTable;
      return updatedTable;
    });
    
    setTables(updatedTables);
    // If the configuration modal is open for this table, keep it in sync
    if (configModalTable && configModalTable.id === tableId && changedTable) {
      setConfigModalTable(changedTable);
    }
  }, [tables]);

  const handleDuplicateTable = () => {
    if (selectedTableIds.size === 0) return;
    
    // Duplicate the first selected table (or all selected tables if desired)
    const firstSelectedId = Array.from(selectedTableIds)[0];
    const tableToDuplicate = tables.find((t) => t.id === firstSelectedId);
    if (!tableToDuplicate) return;

    // Get the original table's priority (default to 999 if undefined)
    const originalPriority = tableToDuplicate.seatNumberPriority ?? 999;
    
    // Create a duplicate with new ID, offset position, and increased seat priority
    const duplicatedTable: TableConfig = {
      ...tableToDuplicate,
      id: `table-${Date.now()}`,
      name: `${tableToDuplicate.name} (Copy)`,
      x: tableToDuplicate.x + 50, // Offset to the right
      y: tableToDuplicate.y + 50, // Offset down
      seatNumberPriority: originalPriority + 1, // Increase priority by 1
      seating: tableToDuplicate.seating.map((seat) => ({
        ...seat,
        rsvpId: null, // Clear guest assignments
        role: null,
      })),
    };

    const calculated = calculateSeatingPositions(duplicatedTable);
    const recalculated = recalculateSeatNumbers([...tables, calculated]);
    setTables(recalculated);
    setSelectedTableIds(new Set([duplicatedTable.id])); // Select the new duplicate
  };

  // Keyboard shortcuts: Delete, Ctrl/Cmd + C (copy) and Ctrl/Cmd + V (paste)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      // Avoid when typing in inputs/textareas/selects
      if (active && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)) return;

      // Handle Delete key
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const sel = selectedTableIdsRef.current;
        if (sel.size === 0) return;

        const tableCount = sel.size;
        const tablesNow = tablesRef.current;
        const tableNames = Array.from(sel)
          .map((id) => tablesNow.find((t) => t.id === id)?.name)
          .filter(Boolean)
          .join(', ');

        confirmAction({
          title: `Delete ${tableCount} Table${tableCount > 1 ? 's' : ''}`,
          text: `Are you sure you want to delete ${tableCount > 1 ? 'these tables' : `"${tableNames}"`}? This action cannot be undone.`,
          icon: 'warning',
        }).then((result) => {
          if (result.isConfirmed) {
            const idsToDelete = Array.from(selectedTableIdsRef.current);
            idsToDelete.forEach((id) => handleDeleteTable(id));
            setSelectedTableIds(new Set());
            showSuccess(`${tableCount} table${tableCount > 1 ? 's' : ''} deleted`);
          }
        });
        e.preventDefault();
        return;
      }

      // Handle modifier key combinations
      const isModifier = e.ctrlKey || e.metaKey;
      if (!isModifier) return;

      if (e.key.toLowerCase() === 'c') {
        const sel = selectedTableIdsRef.current;
        if (sel.size === 0) return;
        const firstSelectedId = Array.from(sel)[0];
        const t = tablesRef.current.find((tb) => tb.id === firstSelectedId);
        if (!t) return;
        const copy: TableConfig = JSON.parse(JSON.stringify(t));
        setClipboardTable(copy);
        showSuccess('Table copied');
        e.preventDefault();
      }

      if (e.key.toLowerCase() === 'v') {
        const clip = clipboardTableRef.current;
        if (!clip) return;
        const originalPriority = clip.seatNumberPriority ?? 999;

        const pasted: TableConfig = {
          ...clip,
          id: `table-${Date.now()}`,
          name: `${clip.name} (Copy)`,
          x: (clip.x || 0) + 40,
          y: (clip.y || 0) + 40,
          seatNumberPriority: originalPriority + 1,
          seating: clip.seating.map((s: any) => ({ ...s, rsvpId: null, role: null })),
        } as TableConfig;

        const calculated = calculateSeatingPositions(pasted);
        const recalculated = recalculateSeatNumbers([...tablesRef.current, calculated]);
        setTables(recalculated);
        setSelectedTableIds(new Set([calculated.id]));
        showSuccess('Table pasted');
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDeleteTable = (tableId: string) => {
    setTables((prev) => {
      const updatedTables = prev.filter((t) => t.id !== tableId);
      return recalculateSeatNumbers(updatedTables);
    });
    setConfigModalTable(null);
    setSelectedTableIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(tableId);
      return newSet;
    });
  };

  const handleSeatClick = (tableId: string, seatNumber: number) => {
    // Prevent click if we just dropped on this seat (within 200ms)
    if (justDroppedRef.current) {
      const { tableId: droppedTableId, seatNumber: droppedSeatNumber, timestamp } = justDroppedRef.current;
      const timeSinceDrop = Date.now() - timestamp;
      if (
        droppedTableId === tableId &&
        droppedSeatNumber === seatNumber &&
        timeSinceDrop < 200
      ) {
        // Clear the ref and ignore this click
        justDroppedRef.current = null;
        return;
      }
      // Clear old refs
      if (timeSinceDrop >= 200) {
        justDroppedRef.current = null;
      }
    }

    const table = tables.find((t) => t.id === tableId);
    if (!table) return;

    const seat = table.seating.find((s) => s.seatNumber === seatNumber);
    if (!seat) return;

    const guest = seat.rsvpId ? allGuests.find((g) => g.id === seat.rsvpId) || null : null;
    setGuestAssignmentModal({
      tableId,
      tableName: table.name,
      seatNumber,
      currentGuest: guest,
    });
  };

  const handleAssignGuest = (tableId: string, seatNumber: number, guestId: string) => {
    setTables(
      tables.map((table) => {
        if (table.id === tableId) {
          return {
            ...table,
            seating: table.seating.map((seat) => {
              if (seat.seatNumber === seatNumber) {
                const guest = allGuests.find((g) => g.id === guestId);
                return {
                  ...seat,
                  rsvpId: guestId,
                  role: guest?.rank || null,
                };
              }
              return seat;
            }),
          };
        }
        // Remove guest from other tables
        return {
          ...table,
          seating: table.seating.map((seat) =>
            seat.rsvpId === guestId
              ? { ...seat, rsvpId: null, role: null }
              : seat
          ),
        };
      })
    );
  };



  // Recalculate seat numbers across all tables based on priority
  // Helper function to determine which side a seat is on based on its position
  const getSeatSide = (seat: { x: number; y: number }, table: TableConfig): string => {
    const { type, width, height } = table;
    
    if (type === 'circular' || type === 'oval') {
      // For circular tables, use angle to preserve position
      const angle = Math.atan2(seat.y, seat.x);
      return `angle_${angle.toFixed(4)}`;
    }
    
    // For rectangular/square tables, determine side
    const chairOffset = 30;
    if (seat.x <= -width / 2 - chairOffset + 10) return 'leftEnd';
    if (seat.x >= width / 2 + chairOffset - 10) return 'rightEnd';
    if (seat.y < 0) return 'top';
    if (seat.y > 0) return 'bottom';
    if (seat.x < 0) return 'left';
    if (seat.x > 0) return 'right';
    return 'unknown';
  };

  // Helper function to preserve guests on their side when seats are recalculated
  type GuestInfo = { rsvpId: string | null; role: string | null; position: number };
  const preserveGuestsBySide = (oldTable: TableConfig, newTable: TableConfig): TableConfig => {
    // Map old seats to their side and guest assignment
    const guestsBySide = new Map<string, GuestInfo[]>();
    const sidePositions = new Map<string, number[]>();
    
    oldTable.seating.forEach((seat, index) => {
      if (seat.rsvpId) {
        const side = getSeatSide(seat, oldTable);
        if (!guestsBySide.has(side)) {
          guestsBySide.set(side, []);
          sidePositions.set(side, []);
        }
        // Store relative position on the side (for ordering)
        const sideSeats = oldTable.seating.filter((s, i) => getSeatSide(s, oldTable) === side);
        const positionOnSide = sideSeats.findIndex((s, i) => i === index || (s.x === seat.x && s.y === seat.y));
        guestsBySide.get(side)!.push({
          rsvpId: seat.rsvpId,
          role: seat.role,
          position: positionOnSide >= 0 ? positionOnSide : sideSeats.length,
        });
        sidePositions.get(side)!.push(seat.x + seat.y * 1000); // Simple position hash
      }
    });
    
    // Now map guests back to new seats on the same side. Start from unassigned seats so we
    // never carry over stale rsvpIds from calculateSeatingPositions when the seat count changes.
    const newSeating = newTable.seating.map((s) => ({
      ...s,
      rsvpId: null as string | null,
      role: null as string | null,
    }));
    const sideIndexMap = new Map<string, number>();
    const assignedGuests = new Set<string>(); // Track which guests have been assigned
    
    newSeating.forEach((seat, index) => {
      const side = getSeatSide(seat, newTable);
      if (!sideIndexMap.has(side)) {
        sideIndexMap.set(side, 0);
      }
      const sideIndex = sideIndexMap.get(side)!;
      
      // Check if there's a guest for this position on this side
      const sideGuests = guestsBySide.get(side);
      if (sideGuests && sideGuests.length > 0) {
        // For circular tables, try to match by closest angle
        if (newTable.type === 'circular' || newTable.type === 'oval') {
          // Find guest with closest angle
          let bestMatch: GuestInfo | null = null;
          let bestDistance = Infinity;
          let bestMatchIndex = -1;
          
          for (let guestIdx = 0; guestIdx < sideGuests.length; guestIdx++) {
            const guest = sideGuests[guestIdx];
            // Use position to find closest match
            const oldSeat = oldTable.seating.find(s => s.rsvpId === guest.rsvpId);
            if (oldSeat) {
              const oldAngle = Math.atan2(oldSeat.y, oldSeat.x);
              const newAngle = Math.atan2(seat.y, seat.x);
              let angleDiff = Math.abs(newAngle - oldAngle);
              if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
              
              if (angleDiff < bestDistance) {
                bestDistance = angleDiff;
                bestMatch = guest;
                bestMatchIndex = guestIdx;
              }
            }
          }
          
          if (bestMatchIndex >= 0 && bestMatch && !assignedGuests.has(bestMatch.rsvpId || '')) {
            newSeating[index] = {
              ...seat,
              rsvpId: bestMatch.rsvpId,
              role: bestMatch.role,
            };
            assignedGuests.add(bestMatch.rsvpId || '');
            // Remove from list to avoid duplicates
            sideGuests.splice(bestMatchIndex, 1);
          }
        } else {
          // For rectangular/square tables, match by position order on the side
          // Sort guests by their original position on the side (only once per side)
          const sortKey = side + '_sorted';
          if (!sideIndexMap.has(sortKey)) {
            sideGuests.sort((a, b) => {
              // Sort by position first
              if (a.position !== b.position) return a.position - b.position;
              // If positions are equal, find original seats and sort by coordinates
              const seatA = oldTable.seating.find(s => s.rsvpId === a.rsvpId);
              const seatB = oldTable.seating.find(s => s.rsvpId === b.rsvpId);
              if (seatA && seatB) {
                // For top/bottom, sort by x; for left/right/ends, sort by y
                const sideType = getSeatSide(seatA, oldTable);
                if (sideType === 'top' || sideType === 'bottom') {
                  return seatA.x - seatB.x;
                } else {
                  return seatA.y - seatB.y;
                }
              }
              return 0;
            });
            sideIndexMap.set(sortKey, 1);
          }
          
          if (sideIndex < sideGuests.length) {
            const guest = sideGuests[sideIndex];
            if (guest && !assignedGuests.has(guest.rsvpId || '')) {
              newSeating[index] = {
                ...seat,
                rsvpId: guest.rsvpId,
                role: guest.role,
              };
              assignedGuests.add(guest.rsvpId || '');
            }
          }
        }
      }
      
      // Each seat along a side consumes one ordered slot; always advance so we do not retry the
      // same guest index (which duplicated assignments) or leave stale rsvpIds on extra seats.
      sideIndexMap.set(side, sideIndex + 1);
    });
    
    return {
      ...newTable,
      seating: newSeating,
    };
  };

  const recalculateSeatNumbers = (tablesToUpdate: TableConfig[]) => {
    // Sort tables by priority (lower number = higher priority)
    const sortedTables = [...tablesToUpdate].sort((a, b) => {
      const priorityA = a.seatNumberPriority || 999;
      const priorityB = b.seatNumberPriority || 999;
      return priorityA - priorityB;
    });

    let globalSeatNumber = 1;
    
    return sortedTables.map(table => {
      const updatedSeating = table.seating.map(seat => ({
        ...seat,
        seatNumber: globalSeatNumber++
      }));
      
      return {
        ...table,
        seating: updatedSeating
      };
    });
  };

  const findGuestAssignment = (guestId: string) => {
    const assignments: Array<{ roomId: string; tableId: string; seatNumber: number }> = [];
    
    for (const [roomId, roomTables] of allRoomTables.entries()) {
      for (const table of roomTables) {
        const seats = table.seating?.filter((s) => s.rsvpId === guestId) || [];
        for (const seat of seats) {
          assignments.push({
            roomId,
            tableId: table.id,
            seatNumber: seat.seatNumber,
          });
        }
      }
    }
    
    if (assignments.length > 0) {
      console.log('🔍 [findGuestAssignment] Found assignments', {
        guestId,
        count: assignments.length,
        assignments,
      });
      
      if (assignments.length > 1) {
        console.warn('⚠️ [findGuestAssignment] Guest is assigned to MULTIPLE seats!', assignments);
      }
      
      // Return the first one found (for backward compatibility)
      return assignments[0];
    }
    
    return null;
  };

  const persistRoomTables = async (roomId: string, tablesToPersist: TableConfig[]) => {
    if (!unitId || !eventId) return;

    try {
      const payload = sanitizeForFirestore({
        tables: tablesToPersist,
        lastUpdated: new Date().toISOString(),
      });

      await setDoc(
        doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId),
        payload,
        { merge: true }
      );
    } catch (error) {
      console.error('Error updating room table plan:', error);
    }
  };

  // Helper function to find seats adjacent to or across from a given seat
  const findAdjacentSeats = (table: TableConfig, seatNumber: number): number[] => {
    const seat = table.seating.find(s => s.seatNumber === seatNumber);
    if (!seat) return [];

    const adjacent: number[] = [];
    const threshold = 60; // Distance threshold for "adjacent" (2x chair radius + some margin)

    for (const otherSeat of table.seating) {
      if (otherSeat.seatNumber === seatNumber) continue;

      const dx = otherSeat.x - seat.x;
      const dy = otherSeat.y - seat.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Adjacent: close horizontally or vertically
      if (distance < threshold) {
        adjacent.push(otherSeat.seatNumber);
      }
    }

    return adjacent;
  };

  const findAcrossSeats = (table: TableConfig, seatNumber: number): number[] => {
    const seat = table.seating.find(s => s.seatNumber === seatNumber);
    if (!seat) return [];

    const across: number[] = [];
    const tableCenter = { x: 0, y: 0 };

    // For circular/oval tables, across means opposite side (180 degrees)
    if (table.type === 'circular' || table.type === 'oval') {
      const seatAngle = Math.atan2(seat.y, seat.x);
      const oppositeAngle = seatAngle + Math.PI;

      for (const otherSeat of table.seating) {
        if (otherSeat.seatNumber === seatNumber) continue;
        const otherAngle = Math.atan2(otherSeat.y, otherSeat.x);
        const angleDiff = Math.abs(otherAngle - oppositeAngle);
        const normalizedDiff = Math.min(angleDiff, 2 * Math.PI - angleDiff);
        
        if (normalizedDiff < Math.PI / 6) { // Within 30 degrees of opposite
          across.push(otherSeat.seatNumber);
        }
      }
    } else {
      // For rectangular/square tables, across means opposite side
      const isOnTop = seat.y < 0;
      const isOnBottom = seat.y > 0;
      const isOnLeft = seat.x < 0;
      const isOnRight = seat.x > 0;

      for (const otherSeat of table.seating) {
        if (otherSeat.seatNumber === seatNumber) continue;

        if (isOnTop && otherSeat.y > 0 && Math.abs(otherSeat.x - seat.x) < table.width / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnBottom && otherSeat.y < 0 && Math.abs(otherSeat.x - seat.x) < table.width / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnLeft && otherSeat.x > 0 && Math.abs(otherSeat.y - seat.y) < table.height / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnRight && otherSeat.x < 0 && Math.abs(otherSeat.y - seat.y) < table.height / 4) {
          across.push(otherSeat.seatNumber);
        }
      }
    }

    return across;
  };

  // Group guests by relationships (invitedBy)
  const groupGuestsByRelationships = (guests: RSVP[]): RSVP[][] => {
    const groups: RSVP[][] = [];
    const processed = new Set<string>();
    const inviterMap = new Map<string, RSVP[]>();

    // Build map of inviter -> invitees
    for (const guest of guests) {
      if (guest.invitedBy) {
        if (!inviterMap.has(guest.invitedBy)) {
          inviterMap.set(guest.invitedBy, []);
        }
        inviterMap.get(guest.invitedBy)!.push(guest);
      }
    }

    // Also group guests who invited others (the inviters themselves)
    for (const guest of guests) {
      if (processed.has(guest.id)) continue;

      const group: RSVP[] = [guest];
      processed.add(guest.id);

      // Add guests invited by this guest
      const invitees = inviterMap.get(guest.id) || [];
      for (const invitee of invitees) {
        if (!processed.has(invitee.id)) {
          group.push(invitee);
          processed.add(invitee.id);
        }
      }

      // If this guest was invited by someone, add them to that group
      if (guest.invitedBy) {
        const inviter = guests.find(g => g.id === guest.invitedBy);
        if (inviter && !processed.has(inviter.id)) {
          group.unshift(inviter);
          processed.add(inviter.id);
        }
      }

      if (group.length > 0) {
        groups.push(group);
      }
    }

    // Sort groups by size (largest first)
    return groups.sort((a, b) => b.length - a.length);
  };

  // Auto-populate algorithm
  const autoPopulateGuests = async (reassignAll: boolean) => {
    if (!selectedRoomId) {
      showError('Please select a room first');
      return;
    }

    try {
      // Step 1: Get guests to assign and prepare tables
      let guestsToAssign: RSVP[];
      let workingTables: TableConfig[];
      
      if (reassignAll) {
        // Unassign all guests first
        workingTables = tables.map(table => ({
          ...table,
          seating: table.seating.map(seat => ({
            ...seat,
            rsvpId: null,
            role: null,
          })),
        }));
        setTables(workingTables);
        
        // Update allRoomTables
        if (selectedRoomId) {
          setAllRoomTables((prev) => {
            const updated = new Map(prev);
            updated.set(selectedRoomId, workingTables);
            return updated;
          });
        }
        
        guestsToAssign = allGuests;
      } else {
        // Only assign unassigned guests
        workingTables = tables.map(table => ({ ...table }));
        const assignedIds = new Set<string>();
        for (const table of tables) {
          for (const seat of table.seating) {
            if (seat.rsvpId) {
              assignedIds.add(seat.rsvpId);
            }
          }
        }
        guestsToAssign = allGuests.filter(g => !assignedIds.has(g.id));
      }

      if (guestsToAssign.length === 0) {
        showSuccess('No guests to assign');
        return;
      }

      // Step 2: Group guests by relationships
      const guestGroups = groupGuestsByRelationships(guestsToAssign);

      // Step 3: Collect all available seats
      const availableSeats: Array<{
        tableId: string;
        table: TableConfig;
        seatNumber: number;
        seat: any;
        adjacentSeats: number[];
        acrossSeats: number[];
      }> = [];

      for (const table of workingTables) {
        for (const seat of table.seating) {
          if (!seat.rsvpId) {
            availableSeats.push({
              tableId: table.id,
              table,
              seatNumber: seat.seatNumber,
              seat,
              adjacentSeats: findAdjacentSeats(table, seat.seatNumber),
              acrossSeats: findAcrossSeats(table, seat.seatNumber),
            });
          }
        }
      }

      // Step 4: Sort guests by priority (large groups, small groups, individuals)
      const sortedGuests: RSVP[] = [];

      // Priority: guests sorted by relationship group size
      const remainingGuests = new Set(guestsToAssign.map(g => g.id));
      const remainingGroups = guestGroups.filter(group => 
        group.some(g => remainingGuests.has(g.id))
      );
      for (const group of remainingGroups.sort((a, b) => b.length - a.length)) {
        for (const guest of group) {
          if (remainingGuests.has(guest.id)) {
            sortedGuests.push(guest);
            remainingGuests.delete(guest.id);
          }
        }
      }

      // Step 5: Assign guests
      const assigned = new Set<string>();
      const updatedTables = workingTables.map(table => ({ ...table }));

      for (const guest of sortedGuests) {
        if (assigned.has(guest.id)) continue;

        let bestSeat: { tableId: string; seatNumber: number } | null = null;
        let bestScore = -1;

        // Find best seat for this guest
        for (const availableSeat of availableSeats) {
          // Skip if seat is already assigned in updatedTables
          const table = updatedTables.find(t => t.id === availableSeat.tableId);
          const seat = table?.seating.find(s => s.seatNumber === availableSeat.seatNumber);
          if (seat?.rsvpId) continue;

          let score = 0;

          // Priority: try to sit with related guests
          if (guest.invitedBy) {
            // Check if inviter is already seated nearby
            const inviterTable = updatedTables.find(t => 
              t.seating.some(s => s.rsvpId === guest.invitedBy)
            );
            if (inviterTable?.id === availableSeat.tableId) {
              const inviterSeat = inviterTable.seating.find(s => s.rsvpId === guest.invitedBy);
              if (inviterSeat) {
                if (availableSeat.adjacentSeats.includes(inviterSeat.seatNumber)) {
                  score += 500;
                } else if (availableSeat.acrossSeats.includes(inviterSeat.seatNumber)) {
                  score += 300;
                } else {
                  score += 100; // Same table but not adjacent
                }
              }
            }
          }

          // Check if any invitees are already seated nearby
          const invitees = guestsToAssign.filter(g => g.invitedBy === guest.id);
          for (const invitee of invitees) {
            if (assigned.has(invitee.id)) {
              const inviteeTable = updatedTables.find(t => 
                t.seating.some(s => s.rsvpId === invitee.id)
              );
              if (inviteeTable?.id === availableSeat.tableId) {
                const inviteeSeat = inviteeTable.seating.find(s => s.rsvpId === invitee.id);
                if (inviteeSeat) {
                  if (availableSeat.adjacentSeats.includes(inviteeSeat.seatNumber)) {
                    score += 400;
                  } else if (availableSeat.acrossSeats.includes(inviteeSeat.seatNumber)) {
                    score += 200;
                  }
                }
              }
            }
          }

          if (score > bestScore) {
            bestScore = score;
            bestSeat = { tableId: availableSeat.tableId, seatNumber: availableSeat.seatNumber };
          }
        }

        // Assign guest to best seat
        if (bestSeat) {
          const table = updatedTables.find(t => t.id === bestSeat!.tableId);
          if (table) {
            const seat = table.seating.find(s => s.seatNumber === bestSeat!.seatNumber);
            if (seat) {
              seat.rsvpId = guest.id;
              seat.role = guest.rank || null;
              assigned.add(guest.id);
            }
          }
        }
      }

      setTables(updatedTables);
      
      // Update allRoomTables
      if (selectedRoomId) {
        setAllRoomTables((prev) => {
          const updated = new Map(prev);
          updated.set(selectedRoomId, updatedTables);
          return updated;
        });
      }
      
      showSuccess(`Auto-populated ${assigned.size} guest(s)`);
    } catch (error) {
      console.error('Error auto-populating guests:', error);
      showError('Failed to auto-populate guests');
    }
  };

  const handleUnassignGuest = (tableId: string, seatNumber: number) => {
    setTables(
      tables.map((table) => {
        if (table.id === tableId) {
          return {
            ...table,
            seating: table.seating.map((seat) =>
              seat.seatNumber === seatNumber
                ? { ...seat, rsvpId: null, role: null }
                : seat
            ),
          };
        }
        return table;
      })
    );
  };

  // Find related guests (those who invited this guest, or who were invited by this guest)
  const findRelatedGuests = (guestId: string): Set<string> => {
    const related = new Set<string>();
    const guest = allGuests.find(g => g.id === guestId);
    if (!guest) return related;

    // Add guests who invited this guest
    if (guest.invitedBy) {
      related.add(guest.invitedBy);
    }

    // Add guests who were invited by this guest
    for (const otherGuest of allGuests) {
      if (otherGuest.invitedBy === guestId) {
        related.add(otherGuest.id);
      }
    }

    return related;
  };

  const handleGuestDragStart = (guest: RSVP, e: React.DragEvent) => {
    setDraggedGuest(guest);
    
    // Find and highlight related guests
    const relatedGuests = findRelatedGuests(guest.id);
    setHighlightedRelatedGuests(relatedGuests);
    
    // Set drag data for HTML5 drag and drop
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', guest.id);
      // Store guest data as JSON for easier retrieval
      e.dataTransfer.setData('application/json', JSON.stringify({ id: guest.id, name: guest.name }));
      
      // Create custom drag image - a circle with guest name (matching seat appearance)
      const dragImage = document.createElement('div');
      dragImage.style.width = '48px';
      dragImage.style.height = '48px';
      dragImage.style.borderRadius = '50%';
      dragImage.style.backgroundColor = '#10b981'; // Green color like occupied seats
      dragImage.style.border = '2px solid #059669';
      dragImage.style.display = 'flex';
      dragImage.style.flexDirection = 'column';
      dragImage.style.alignItems = 'center';
      dragImage.style.justifyContent = 'center';
      dragImage.style.opacity = '0.5';
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px'; // Position off-screen
      dragImage.style.left = '-1000px';
      dragImage.style.pointerEvents = 'none';
      dragImage.style.zIndex = '10000';
      dragImage.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
      
      // Split name into first and last name (matching seat display)
      const nameParts = guest.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Create container for name text
      const nameContainer = document.createElement('div');
      nameContainer.style.display = 'flex';
      nameContainer.style.flexDirection = 'column';
      nameContainer.style.alignItems = 'center';
      nameContainer.style.justifyContent = 'center';
      nameContainer.style.width = '100%';
      nameContainer.style.height = '100%';
      nameContainer.style.padding = '2px';
      
      // Add first name
      if (firstName) {
        const firstNameText = document.createElement('div');
        firstNameText.textContent = firstName;
        firstNameText.style.color = '#000000';
        firstNameText.style.fontSize = '9px';
        firstNameText.style.fontWeight = 'bold';
        firstNameText.style.textAlign = 'center';
        firstNameText.style.lineHeight = '10px';
        firstNameText.style.overflow = 'hidden';
        firstNameText.style.textOverflow = 'ellipsis';
        firstNameText.style.maxWidth = '44px';
        firstNameText.style.whiteSpace = 'nowrap';
        nameContainer.appendChild(firstNameText);
      }
      
      // Add last name if it exists
      if (lastName) {
        const lastNameText = document.createElement('div');
        lastNameText.textContent = lastName;
        lastNameText.style.color = '#000000';
        lastNameText.style.fontSize = '9px';
        lastNameText.style.fontWeight = 'bold';
        lastNameText.style.textAlign = 'center';
        lastNameText.style.lineHeight = '10px';
        lastNameText.style.overflow = 'hidden';
        lastNameText.style.textOverflow = 'ellipsis';
        lastNameText.style.maxWidth = '44px';
        lastNameText.style.whiteSpace = 'nowrap';
        nameContainer.appendChild(lastNameText);
      }
      
      // If no name parts, show initials as fallback
      if (!firstName && !lastName) {
        const initialsText = document.createElement('div');
        initialsText.textContent = guest.name.slice(0, 2).toUpperCase();
        initialsText.style.color = '#000000';
        initialsText.style.fontSize = '12px';
        initialsText.style.fontWeight = 'bold';
        initialsText.style.textAlign = 'center';
        nameContainer.appendChild(initialsText);
      }
      
      dragImage.appendChild(nameContainer);
      document.body.appendChild(dragImage);
      
      // Set the drag image, offset by half the size so cursor is at center
      e.dataTransfer.setDragImage(dragImage, 24, 24);
      
      // Clean up the drag image after a short delay
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage);
        }
      }, 0);
    }
  };

  const handleGuestDragEnd = () => {
    // Clear dragged guest after a short delay to allow drop event to process
    // If drop was successful, handleSeatDrop will have already cleared it
    setTimeout(() => {
      setDraggedGuest(null);
      setDraggedSeat(null);
      setHighlightedRelatedGuests(new Set());
    }, 100);
  };

  const handleSeatDrop = (tableId: string, seatNumber: number, overrideGuest?: RSVP | null) => {
    // Use override guest if provided, otherwise use state
    const guestToUse: RSVP | null = overrideGuest !== undefined ? overrideGuest : draggedGuest;
    
    devLog('🎯 [handleSeatDrop] Called', {
      tableId,
      seatNumber,
      hasDraggedSeat: !!draggedSeat,
      hasDraggedGuest: !!draggedGuest,
      hasOverrideGuest: overrideGuest !== undefined,
      guestToUse: guestToUse ? { id: guestToUse.id, name: guestToUse.name } : null,
      draggedSeat,
      stateDraggedGuest: draggedGuest ? { id: draggedGuest.id, name: draggedGuest.name } : null,
    });

    // Handle seat-to-seat drag (swapping)
    if (draggedSeat) {
      devLog('🔄 [handleSeatDrop] Handling seat-to-seat drag');
      const sourceTable = tables.find(t => t.id === draggedSeat.tableId);
      const targetTable = tables.find(t => t.id === tableId);
      
      if (!sourceTable || !targetTable) {
        resetSeatDragState();
        return;
      }
      
      const sourceSeat = sourceTable.seating.find(s => s.seatNumber === draggedSeat.seatNumber);
      const targetSeat = targetTable.seating.find(s => s.seatNumber === seatNumber);
      
      if (!sourceSeat || !targetSeat) {
        resetSeatDragState();
        return;
      }
      
      // If dropping on the same seat, do nothing but clear drag state
      if (draggedSeat.tableId === tableId && draggedSeat.seatNumber === seatNumber) {
        // Mark that we just dropped on this seat to prevent click event
        justDroppedRef.current = {
          tableId,
          seatNumber,
          timestamp: Date.now(),
        };
        resetSeatDragState();
        return;
      }
      
      const sourceGuestId = sourceSeat.rsvpId;
      const targetGuestId = targetSeat.rsvpId;
      const sourceGuest = sourceGuestId ? allGuests.find(g => g.id === sourceGuestId) : null;
      const targetGuest = targetGuestId ? allGuests.find(g => g.id === targetGuestId) : null;
      
      // Swap guests if target seat is occupied, otherwise just move
      const updatedTables = tables.map((table) => {
        if (table.id === draggedSeat.tableId && table.id === tableId) {
          // Same table - swap within table
          return {
            ...table,
            seating: table.seating.map((seat) => {
              if (seat.seatNumber === draggedSeat.seatNumber) {
                return { ...seat, rsvpId: targetGuestId, role: targetGuest?.rank || null };
              }
              if (seat.seatNumber === seatNumber) {
                return { ...seat, rsvpId: sourceGuestId, role: sourceGuest?.rank || null };
              }
              return seat;
            }),
          };
        } else if (table.id === draggedSeat.tableId) {
          // Source table - clear source seat or swap with target guest
          return {
      ...table,
      seating: table.seating.map((seat) =>
              seat.seatNumber === draggedSeat.seatNumber
                ? { ...seat, rsvpId: targetGuestId, role: targetGuest?.rank || null }
          : seat
      ),
          };
        } else if (table.id === tableId) {
          // Target table - assign source guest or swap
        return {
          ...table,
          seating: table.seating.map((seat) =>
            seat.seatNumber === seatNumber
                ? { ...seat, rsvpId: sourceGuestId, role: sourceGuest?.rank || null }
              : seat
          ),
          };
        }
        return table;
      });
      
      setTables(updatedTables);
      
      // Mark that we just dropped on this seat to prevent click event
      justDroppedRef.current = {
        tableId,
        seatNumber,
        timestamp: Date.now(),
      };
      resetSeatDragState();
      
      if (targetGuestId && sourceGuest) {
        showSuccess(`${sourceGuest.name} and ${targetGuest?.name} swapped seats`);
      } else if (sourceGuest) {
        showSuccess(`${sourceGuest.name} moved to seat ${seatNumber}`);
      }
      return;
    }
    
    // Handle sidebar-to-seat drag
    if (!guestToUse) {
      devLog('❌ [handleSeatDrop] No guest to assign, returning');
      return;
    }
    
    // Use guestToUse for the rest of the function (rename for clarity in this scope)
    const activeGuest = guestToUse;

    devLog('🎯 [DROP] Starting drop handler', {
      guestId: activeGuest.id,
      guestName: activeGuest.name,
      targetTableId: tableId,
      targetSeatNumber: seatNumber,
      selectedRoomId,
    });

    const currentAssignment = findGuestAssignment(activeGuest.id);
    const oldRoomId = currentAssignment?.roomId ?? null;
    const oldTableId = currentAssignment?.tableId ?? null;
    const oldSeatNumber = currentAssignment?.seatNumber ?? null;

    devLog('📍 [DROP] Current assignment found', {
      oldRoomId,
      oldTableId,
      oldSeatNumber,
      currentAssignment,
    });

    const isSameSeat =
      oldRoomId === selectedRoomId &&
      oldTableId === tableId &&
      oldSeatNumber === seatNumber;

    if (isSameSeat) {
      devLog('⏭️ [DROP] Same seat, skipping');
      justDroppedRef.current = {
        tableId,
        seatNumber,
        timestamp: Date.now(),
      };
      setDraggedGuest(null);
      setIsOverUnassignZone(false);
      return;
    }

    const targetTable = tables.find((t: TableConfig) => t.id === tableId);
    const targetSeat = targetTable?.seating.find((s: any) => s.seatNumber === seatNumber);
    const targetGuestId = targetSeat?.rsvpId;
    const targetGuest = targetGuestId ? allGuests.find((g: RSVP) => g.id === targetGuestId) : null;

    devLog('🎯 [DROP] Target seat info', {
      targetTableId: tableId,
      targetSeatNumber: seatNumber,
      targetGuestId,
      targetGuestName: targetGuest?.name,
      targetSeatBefore: targetSeat,
    });

    // STEP 1: Loop through ALL rooms, ALL tables, ALL seats to unassign the guest
    const updatedAllRooms = new Map<string, TableConfig[]>();
    const roomsToPersist = new Map<string, TableConfig[]>();
    let totalUnassignments = 0;

    setAllRoomTables((prev) => {
      devLog('🔄 [DROP] STEP 1: Starting unassignment loop', {
        totalRooms: prev.size,
        roomIds: Array.from(prev.keys()),
      });

      // Loop through every room
      for (const [roomId, roomTables] of prev.entries()) {
        devLog(`  🏠 [DROP] Checking room: ${roomId}`, {
          tableCount: roomTables.length,
        });

        let roomChanged = false;
        const updatedRoomTables: TableConfig[] = [];

        // Loop through every table in this room
        for (const table of roomTables) {
          let tableChanged = false;
          const updatedSeating: any[] = [];
          let seatsUnassignedInTable = 0;

          // Loop through every seat in this table
          for (const seat of table.seating) {
            if (seat.rsvpId === activeGuest.id) {
              // Unassign the guest from this seat
              devLog(`    ❌ [DROP] Unassigning guest from seat`, {
                roomId,
                tableId: table.id,
                tableName: table.name,
                seatNumber: seat.seatNumber,
                guestId: seat.rsvpId,
              });
              tableChanged = true;
              roomChanged = true;
              totalUnassignments++;
              seatsUnassignedInTable++;
              updatedSeating.push({
                ...seat,
                rsvpId: null,
                role: null,
              });
            } else {
              // Keep the seat as-is
              updatedSeating.push({ ...seat });
            }
          }

          if (tableChanged) {
            devLog(`    ✅ [DROP] Table ${table.name} had ${seatsUnassignedInTable} seat(s) unassigned`);
            updatedRoomTables.push({
              ...table,
              seating: updatedSeating,
            });
          } else {
            updatedRoomTables.push(table);
          }
        }

        if (roomChanged) {
          devLog(`  ✅ [DROP] Room ${roomId} was updated`);
          updatedAllRooms.set(roomId, updatedRoomTables);
          roomsToPersist.set(roomId, updatedRoomTables);
        } else {
          devLog(`  ⏭️ [DROP] Room ${roomId} had no changes`);
          updatedAllRooms.set(roomId, roomTables);
        }
      }

      devLog('✅ [DROP] STEP 1 Complete', {
        totalUnassignments,
        roomsUpdated: roomsToPersist.size,
        updatedRoomIds: Array.from(roomsToPersist.keys()),
      });

      return updatedAllRooms;
    });

    // Persist updates for all rooms that had the guest removed (except current room)
    devLog('💾 [DROP] Persisting updates for rooms', {
      roomsToPersist: Array.from(roomsToPersist.keys()),
      selectedRoomId,
    });
    for (const [roomId, roomTables] of roomsToPersist.entries()) {
      if (roomId !== selectedRoomId) {
        devLog(`  💾 [DROP] Persisting room ${roomId}`);
        void persistRoomTables(roomId, roomTables);
      } else {
        devLog(`  ⏭️ [DROP] Skipping persist for current room ${roomId} (will update below)`);
      }
    }

    // STEP 2: Get the updated current room tables (with guest already removed)
    devLog('🔄 [DROP] STEP 2: Getting updated current room tables', {
      selectedRoomId,
      hasUpdatedRooms: updatedAllRooms.has(selectedRoomId || ''),
    });
    const updatedCurrentRoomTables = updatedAllRooms.get(selectedRoomId || '') || tables;
    
    devLog('📊 [DROP] Current room tables state', {
      tableCount: updatedCurrentRoomTables.length,
      tables: updatedCurrentRoomTables.map(t => ({
        id: t.id,
        name: t.name,
        seatsWithGuest: t.seating.filter(s => s.rsvpId === activeGuest.id).map(s => s.seatNumber),
      })),
    });

    // STEP 3: Assign the guest to the new seat in the current room
    devLog('🔄 [DROP] STEP 3: Assigning guest to new seat', {
      targetTableId: tableId,
      targetSeatNumber: seatNumber,
    });
    const finalTables: TableConfig[] = updatedCurrentRoomTables.map((table: TableConfig) => {
      if (table.id === tableId) {
        // This is the target table - assign guest to target seat
        const updatedSeating = table.seating.map((seat: any) => {
            if (seat.seatNumber === seatNumber) {
              devLog(`  ✅ [DROP] Assigning guest to seat ${seatNumber} in table ${table.name}`, {
                seatBefore: seat,
              });
              return {
                ...seat,
                rsvpId: activeGuest.id,
                role: activeGuest.rank || null,
              };
            }
          return seat;
        });
        
        devLog(`  ✅ [DROP] Target table ${table.name} updated`, {
          seatingCount: updatedSeating.length,
          assignedSeat: updatedSeating.find(s => s.seatNumber === seatNumber),
        });
        
        return {
          ...table,
          seating: updatedSeating,
        };
      }
      // Other tables remain unchanged (guest already removed in step 1)
      return table;
    });

    devLog('📊 [DROP] Final tables state', {
      tableCount: finalTables.length,
      tables: finalTables.map(t => ({
        id: t.id,
        name: t.name,
        seatsWithGuest: t.seating.filter(s => s.rsvpId === activeGuest.id).map(s => ({
          seatNumber: s.seatNumber,
          rsvpId: s.rsvpId,
        })),
      })),
    });

    setTables(finalTables);

    // Update allRoomTables with the final current room tables
    if (selectedRoomId) {
      devLog('🔄 [DROP] Updating allRoomTables with final state');
      setAllRoomTables((prev) => {
        const updated = new Map(prev);
        updated.set(selectedRoomId, finalTables);
        devLog('✅ [DROP] allRoomTables updated', {
          totalRooms: updated.size,
          currentRoomState: updated.get(selectedRoomId)?.map(t => ({
            id: t.id,
            name: t.name,
            seatsWithGuest: t.seating.filter(s => s.rsvpId === activeGuest.id).map(s => s.seatNumber),
          })),
        });
        return updated;
      });
    }

    justDroppedRef.current = {
      tableId,
      seatNumber,
      timestamp: Date.now(),
    };

    setDraggedGuest(null);
    setIsOverUnassignZone(false);
    setHighlightedRelatedGuests(new Set());

    // Final verification - check if guest is still assigned to multiple seats
    const finalCheck = findGuestAssignment(activeGuest.id);
    devLog('🔍 [DROP] Final verification', {
      guestId: activeGuest.id,
      guestName: activeGuest.name,
      finalAssignment: finalCheck,
      expectedTableId: tableId,
      expectedSeatNumber: seatNumber,
      matches: finalCheck?.tableId === tableId && finalCheck?.seatNumber === seatNumber,
    });

    if (targetGuest) {
      showSuccess(`${activeGuest.name} and ${targetGuest.name} swapped seats`);
    } else {
      showSuccess(`${activeGuest.name} assigned to seat ${seatNumber}`);
    }

    devLog('✅ [DROP] Drop handler complete');
  };

  const handleSeatDragStart = (tableId: string, seatNumber: number, guestId: string) => {
    setDraggedSeat({ tableId, seatNumber, guestId });
    setIsDraggingSeat(true);
    
    // Find and highlight related guests
    const relatedGuests = findRelatedGuests(guestId);
    setHighlightedRelatedGuests(relatedGuests);
    
    // Get guest name for drag preview
    const guest = allGuests.find(g => g.id === guestId);
    if (guest) {
      // Create drag preview that will follow mouse
      const handleMouseMove = (e: MouseEvent) => {
        queueDragPreviewUpdate({
          x: e.clientX,
          y: e.clientY,
          guestName: guest.name,
        });
        // Check if over unassign zone (below header)
        updateUnassignZone(e.clientY >= 64 && e.clientY < 144);
      };
      
      const handleMouseUp = (e: MouseEvent) => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        
        // Check if dropped in unassign zone (below header)
        if (e.clientY >= 64 && e.clientY < 144) {
          // Unassign guest from seat
          const sourceTable = tables.find(t => t.id === tableId);
          if (sourceTable) {
            const updatedTables = tables.map((table) => {
              if (table.id === tableId) {
                return {
                  ...table,
                  seating: table.seating.map((seat) =>
                    seat.seatNumber === seatNumber
                      ? { ...seat, rsvpId: null, role: null }
                      : seat
                  ),
                };
              }
              return table;
            });
            setTables(updatedTables);
            showSuccess(`${guest.name} unassigned from seat`);
          }
          resetSeatDragState();
        }
        // Otherwise, let the stage's onMouseUp handler handle the drop
        // It will call handleSeatDrop which will clear the state
      };
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
  };

  // Handle HTML5 drop events on the stage container
  const handleStageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    devLog('🎯 [handleStageDrop] Called', {
      clientX: e.clientX,
      clientY: e.clientY,
      hasDraggedSeat: !!draggedSeat,
      hasDraggedGuest: !!draggedGuest,
    });
    
    // Always try to get guest data from the drag event as a fallback
    // This ensures we can handle the drop even if state was cleared
    const guestId = e.dataTransfer?.getData('text/plain');
    let guestFromEvent: RSVP | null = null;
    if (guestId) {
      guestFromEvent = allGuests.find(g => g.id === guestId) || null;
    }
    
    // Use draggedGuest from state, or fall back to guest from event
    const activeDraggedGuest = draggedGuest || guestFromEvent;
    
    devLog('👤 [handleStageDrop] Active dragged guest', {
      fromState: !!draggedGuest,
      fromEvent: !!guestFromEvent,
      guest: activeDraggedGuest ? { id: activeDraggedGuest.id, name: activeDraggedGuest.name } : null,
    });

    // Check if dropped in unassign zone (below header, top 80px area)
    // Header is typically around 64px, so zone starts at ~80px
    if (e.clientY >= 64 && e.clientY < 144) {
      if (draggedSeat) {
        // Unassign guest from seat
        const sourceTable = tables.find(t => t.id === draggedSeat.tableId);
        if (sourceTable) {
          const updatedTables = tables.map((table) => {
            if (table.id === draggedSeat.tableId) {
              return {
                ...table,
                seating: table.seating.map((seat) =>
                  seat.seatNumber === draggedSeat.seatNumber
                    ? { ...seat, rsvpId: null, role: null }
                    : seat
                ),
              };
            }
            return table;
          });
          setTables(updatedTables);
          
          const guest = allGuests.find(g => g.id === draggedSeat.guestId);
          if (guest) {
            showSuccess(`${guest.name} unassigned from seat`);
          }
        }
        resetSeatDragState();
        return;
      } else if (activeDraggedGuest) {
        // Unassign guest (remove from any seat they're in)
        const updatedTables = tables.map((table) => ({
          ...table,
          seating: table.seating.map((seat) =>
            seat.rsvpId === activeDraggedGuest.id
              ? { ...seat, rsvpId: null, role: null }
              : seat
          ),
        }));
        setTables(updatedTables);
        setDraggedGuest(null);
        setIsOverUnassignZone(false);
        setHighlightedRelatedGuests(new Set());
        showSuccess(`${activeDraggedGuest.name} unassigned from seat`);
        return;
      }
    }

    // Handle seat-to-seat drag
    if (draggedSeat && stageRef.current) {
      const stage = stageRef.current;
      const container = stage.container();
      const rect = container.getBoundingClientRect();
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const stageX = (x - stagePos.x) / scale;
      const stageY = (y - stagePos.y) / scale;
      
      const seatRadius = 24;
      let foundSeat: { tableId: string; seatNumber: number } | null = null;
      
      for (const table of tables) {
        const rotRad = (table.rotation * Math.PI) / 180;
        const cos = Math.cos(-rotRad);
        const sin = Math.sin(-rotRad);
        
        const dx = stageX - table.x;
        const dy = stageY - table.y;
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        
        for (const seat of table.seating) {
          const seatX = seat.x;
          const seatY = seat.y;
          const distance = Math.sqrt(
            Math.pow(localX - seatX, 2) + Math.pow(localY - seatY, 2)
          );
          
          if (distance <= seatRadius) {
            foundSeat = { tableId: table.id, seatNumber: seat.seatNumber };
            break;
          }
        }
        
        if (foundSeat) break;
      }
      
      if (foundSeat) {
        handleSeatDrop(foundSeat.tableId, foundSeat.seatNumber);
        resetSeatDragState();
      } else {
        resetSeatDragState();
      }
      return;
    }

    // Handle sidebar-to-seat drag
    // Use the active dragged guest (from state or event)
    if (!activeDraggedGuest || !stageRef.current) {
      setDraggedGuest(null);
      setHighlightedRelatedGuests(new Set());
      return;
    }
    
    // Ensure state is set for consistency
    if (!draggedGuest && activeDraggedGuest) {
      setDraggedGuest(activeDraggedGuest);
    }

    const stage = stageRef.current;
    const container = stage.container();
    const rect = container.getBoundingClientRect();
    
    // Get drop position relative to container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert to stage coordinates
    const stageX = (x - stagePos.x) / scale;
    const stageY = (y - stagePos.y) / scale;
    
    // Find which seat is at this position
    const seatRadius = 24; // Chair radius
    let foundSeat: { tableId: string; seatNumber: number } | null = null;
    
    for (const table of tables) {
      // Account for table rotation
      const rotRad = (table.rotation * Math.PI) / 180;
      const cos = Math.cos(-rotRad);
      const sin = Math.sin(-rotRad);
      
      // Transform point to table's local coordinate system
      const dx = stageX - table.x;
      const dy = stageY - table.y;
      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;
      
      // Check each seat
      for (const seat of table.seating) {
        // Seat position in table's local coordinates
        const seatX = seat.x;
        const seatY = seat.y;
        const distance = Math.sqrt(
          Math.pow(localX - seatX, 2) + Math.pow(localY - seatY, 2)
        );
        
        if (distance <= seatRadius) {
          foundSeat = { tableId: table.id, seatNumber: seat.seatNumber };
          break;
        }
      }
      
      if (foundSeat) break;
    }
    
    if (foundSeat) {
      devLog('✅ [handleStageDrop] Found seat, delegating to handleSeatDrop', {
        tableId: foundSeat.tableId,
        seatNumber: foundSeat.seatNumber,
        activeDraggedGuest: activeDraggedGuest ? { id: activeDraggedGuest.id, name: activeDraggedGuest.name } : null,
      });
      
      // Ensure draggedGuest is set for consistency
      if (!draggedGuest && activeDraggedGuest) {
        setDraggedGuest(activeDraggedGuest);
      }
      
      // Delegate to handleSeatDrop which has the comprehensive unassignment logic
      // Pass the guest directly to avoid state timing issues
      handleSeatDrop(foundSeat.tableId, foundSeat.seatNumber, activeDraggedGuest);
    } else {
      devLog('❌ [handleStageDrop] No seat found at drop location');
      setDraggedGuest(null);
      setHighlightedRelatedGuests(new Set());
    }
  };

  const handleStageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if over unassign zone (below header)
    updateUnassignZone(e.clientY >= 64 && e.clientY < 144);
    
    if (e.dataTransfer) {
      // Always allow drop if we're dragging a guest or seat
      if (isDraggingSeat || draggedGuest) {
        e.dataTransfer.dropEffect = 'move';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    }
  };

  const getGuestName = useCallback((rsvpId: string | null): string | null => {
    if (!rsvpId) return null;
    const guest = allGuests.find((g) => g.id === rsvpId);
    return guest ? guest.name : null;
  }, [allGuests]);

  const { availableGuests, assignedGuests } = useMemo(() => {
    const assignedGuestIds = new Set<string>();

    allRoomTables.forEach((roomTables) => {
      roomTables.forEach((table: any) => {
        if (table.seating) {
          table.seating.forEach((seat: any) => {
            if (seat.rsvpId) {
              assignedGuestIds.add(seat.rsvpId);
            }
          });
        }
      });
    });

    const nextAvailableGuests: RSVP[] = [];
    const nextAssignedGuests: RSVP[] = [];
    allGuests.forEach((guest) => {
      if (assignedGuestIds.has(guest.id)) {
        nextAssignedGuests.push(guest);
      } else {
        nextAvailableGuests.push(guest);
      }
    });

    return {
      availableGuests: nextAvailableGuests,
      assignedGuests: nextAssignedGuests,
    };
  }, [allRoomTables, allGuests]);

  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 3);
    setScale(newScale);
    scheduleViewportAutoSave();
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale / 1.2, 0.3);
    setScale(newScale);
    scheduleViewportAutoSave();
  };

  const handleResetView = () => {
    setScale(1);
    setStagePos({ x: 0, y: 0 });
    scheduleViewportAutoSave();
  };

  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();

    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = scale;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale,
    };

    const newScale = e.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
    const clampedScale = Math.max(0.3, Math.min(3, newScale));

    setScale(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
    scheduleViewportAutoSave();
  };

  const handleSave = async () => {
    if (!unitId || !eventId) {
      showError('Invalid event');
      return;
    }

    setSaving(true);
    try {
      const visualLayoutData = {
        tables,
        viewport: {
          scale,
          position: stagePos,
        },
        lastUpdated: new Date().toISOString(),
      };
      
      const compatibilityData = {
        tables: tables.map((t) => ({
          id: t.id,
          type: t.type,
          seats: t.seats,
          name: t.name,
          seating: t.seating.map((s) => ({
            seatNumber: s.seatNumber,
            rsvpId: s.rsvpId,
            role: s.role,
          })),
        })),
        lastUpdated: new Date().toISOString(),
      };
      
      // Sanitize data to remove undefined values
      const sanitizedVisualLayout = sanitizeForFirestore(visualLayoutData);
      const sanitizedCompatibility = sanitizeForFirestore(compatibilityData);
      
      await setDoc(
        doc(db, 'units', unitId, 'events', eventId, 'tablePlan', 'visualLayout'),
        sanitizedVisualLayout
      );

      // Also save in the old format for compatibility
      await setDoc(
        doc(db, 'units', unitId, 'events', eventId, 'tablePlan', 'layout'),
        sanitizedCompatibility
      );

      showSuccess('Table layout saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      showError('Failed to save table layout');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!unitId) return;

    const result = await Swal.fire({
      title: 'Save as Template',
      text: 'Enter a name for this table layout template:',
      input: 'text',
      inputPlaceholder: 'e.g., Standard 50-Person Layout',
      showCancelButton: true,
      confirmButtonText: 'Save Template',
      confirmButtonColor: '#1E3A5F',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed && result.value) {
      try {
        const templateData = {
          name: result.value,
          tables: tables.map((t) => {
            // Save all table configuration properties
            const tableData: any = {
            type: t.type,
            seats: t.seats,
            name: t.name,
              x: t.x,
              y: t.y,
            width: t.width,
            height: t.height,
              rotation: t.rotation || 0,
              color: t.color || '#ffffff',
              chairColor: t.chairColor || '#e5e7eb',
              seatNumberPriority: t.seatNumberPriority ?? 999,
            };

            // Save seat placement configuration
            if (t.seatPlacement) {
              tableData.seatPlacement = {
                bothSides: t.seatPlacement.bothSides ?? false,
                oneSide: t.seatPlacement.oneSide ?? false,
                leftEnd: t.seatPlacement.leftEnd ?? false,
                rightEnd: t.seatPlacement.rightEnd ?? false,
                noSeats: t.seatPlacement.noSeats ?? false,
                // Square table specific options
                topSide: t.seatPlacement.topSide,
                bottomSide: t.seatPlacement.bottomSide,
                leftSide: t.seatPlacement.leftSide,
                rightSide: t.seatPlacement.rightSide,
              };
            }

            // Save per-side seat counts (important for custom seat distributions)
            if (t.seatCounts) {
              tableData.seatCounts = {
                top: t.seatCounts.top,
                bottom: t.seatCounts.bottom,
                left: t.seatCounts.left,
                right: t.seatCounts.right,
                leftEnd: t.seatCounts.leftEnd,
                rightEnd: t.seatCounts.rightEnd,
              };
            }

            // Persist seat metadata with template
            if (Array.isArray(t.seating)) {
              tableData.seating = t.seating.map((seat: any, index: number) => ({
                index,
                seatNumber: seat?.seatNumber ?? index + 1,
              }));
            }

            return tableData;
          }),
          createdAt: new Date().toISOString(),
        };
        
        // Sanitize data to remove undefined values
        const sanitizedTemplate = sanitizeForFirestore(templateData);
        
        await addDoc(collection(db, 'units', unitId, 'tableTemplates'), sanitizedTemplate);

        showSuccess(`Template "${result.value}" saved!`);
      } catch (error) {
        console.error('Error saving template:', error);
        showError('Failed to save template');
      }
    }
  };

  const handleLoadTemplate = async () => {
    if (!unitId) return;

    try {
      const templatesSnapshot = await getDocs(
        collection(db, 'units', unitId, 'tableTemplates')
      );

      if (templatesSnapshot.empty) {
        showError('No templates found');
        return;
      }

      const templateOptions: Record<string, string> = {};
      templatesSnapshot.forEach((docSnap) => {
        templateOptions[docSnap.id] = docSnap.data().name;
      });

      const result = await Swal.fire({
        title: 'Load Template',
        text: 'Select a template to load:',
        input: 'select',
        inputOptions: templateOptions,
        showCancelButton: true,
        confirmButtonText: 'Load Template',
        confirmButtonColor: '#1E3A5F',
        cancelButtonColor: '#6c757d',
      });

      if (result.isConfirmed && result.value) {
        const templateDoc = await getDoc(
          doc(db, 'units', unitId, 'tableTemplates', result.value)
        );

        if (templateDoc.exists()) {
          const templateData = templateDoc.data();
          const newTables: TableConfig[] = templateData.tables.map((t: any, index: number) => {
            // Use saved position if available, otherwise use grid layout
            const savedX = t.x !== undefined ? t.x : 200 + (index % 3) * 300;
            const savedY = t.y !== undefined ? t.y : 200 + Math.floor(index / 3) * 300;
            
            const baseTable: TableConfig = {
              id: `table-${Date.now()}-${index}`,
              type: t.type,
              seats: t.seats,
              name: t.name || `Table ${index + 1}`,
              x: savedX,
              y: savedY,
              rotation: t.rotation ?? 0,
              width: t.width ?? 150,
              height: t.height ?? 150,
              color: t.color ?? '#ffffff',
              chairColor: t.chairColor ?? '#e5e7eb',
              seatNumberPriority: t.seatNumberPriority ?? (index + 1),
              // Restore seat placement configuration
              seatPlacement: t.seatPlacement || {
                bothSides: true,
                oneSide: false,
                leftEnd: false,
                rightEnd: false,
                noSeats: false,
                // Square table defaults
                topSide: true,
                bottomSide: true,
                leftSide: true,
                rightSide: true,
              },
              // Restore per-side seat counts if available
              seatCounts: t.seatCounts ? {
                top: t.seatCounts.top,
                bottom: t.seatCounts.bottom,
                left: t.seatCounts.left,
                right: t.seatCounts.right,
                leftEnd: t.seatCounts.leftEnd,
                rightEnd: t.seatCounts.rightEnd,
              } : undefined,
              // Initialize seating array (will be recalculated)
              seating: Array.from({ length: t.seats }, (_, i) => ({
                seatNumber: i + 1,
                rsvpId: null,
                role: null,
                x: 0,
                y: 0,
                tag: null,
              })),
            };
            // Recalculate seating positions based on the restored configuration
            const recalculatedTable = calculateSeatingPositions(baseTable);

            return recalculatedTable;
          });

          const recalculatedTables = recalculateSeatNumbers(newTables);
          setTables(recalculatedTables);
          showSuccess('Template loaded successfully!');
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
      showError('Failed to load template');
    }
  };

  /** Export the current table planner canvas exactly as shown on screen (no re-render). */
  const handleExportPDF = async () => {
    const stage = stageRef.current;
    if (!stage) {
      showError('Cannot export: canvas not ready');
      return;
    }

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const stageAspect = stageWidth / stageHeight;
      const pageAspect = pageW / pageH;
      let imgW: number;
      let imgH: number;
      let x: number;
      let y: number;
      if (stageAspect >= pageAspect) {
        imgW = pageW;
        imgH = pageW / stageAspect;
        x = 0;
        y = (pageH - imgH) / 2;
      } else {
        imgH = pageH;
        imgW = pageH * stageAspect;
        x = (pageW - imgW) / 2;
        y = 0;
      }

      // Print-quality bitmap: target ~300 DPI for the image area on the PDF page.
      const MM_PER_INCH = 25.4;
      const PRINT_DPI = 300;
      const pixelRatio = Math.min(
        8,
        Math.max(
          ((imgW / MM_PER_INCH) * PRINT_DPI) / stageWidth,
          ((imgH / MM_PER_INCH) * PRINT_DPI) / stageHeight
        )
      );

      const dataUrl = stage.toDataURL({
        pixelRatio: Math.max(1, pixelRatio),
        mimeType: 'image/png',
        quality: 1.0,
      });

      pdf.addImage(dataUrl, 'PNG', x, y, imgW, imgH);

      pdf.addPage('a4', 'landscape');
      pdf.setFontSize(16);
      pdf.text('Seating Assignments', 20, 30);

      let yPos = 50;
      pdf.setFontSize(10);

      tables.forEach((table) => {
        if (yPos > 550) {
          pdf.addPage('a4', 'landscape');
          yPos = 30;
        }
        pdf.setFontSize(12);
        pdf.text(table.name, 20, yPos);
        yPos += 15;
        pdf.setFontSize(9);
        table.seating.forEach((seat) => {
          if (seat.rsvpId) {
            const guestName = getGuestName(seat.rsvpId);
            if (guestName) {
              pdf.text(`  Seat ${seat.seatNumber}: ${guestName}`, 30, yPos);
              yPos += 12;
              if (yPos > 550) {
                pdf.addPage('a4', 'landscape');
                yPos = 30;
              }
            }
          }
        });
        yPos += 10;
      });

      pdf.save(`table-layout-${eventId}.pdf`);
      showSuccess('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const handleExportPDFCustom = () => {
    setShowPDFExportModal(true);
  };

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      <Header />
      
      {/* Unassign Zone - appears when dragging a guest */}
      {(isDraggingSeat || draggedGuest) && (
        <div
          className={`fixed top-16 left-0 right-0 h-20 z-40 transition-all duration-200 ${
            isOverUnassignZone
              ? 'bg-red-100 border-b-4 border-red-500'
              : 'bg-gray-100 border-b-2 border-gray-300'
          }`}
          style={{
            pointerEvents: 'none',
          }}
        >
          <div className="flex items-center justify-center h-full">
            <div className={`text-center px-4 py-2 rounded-lg ${
              isOverUnassignZone
                ? 'bg-red-500 text-white'
                : 'bg-white text-gray-600 border-2 border-dashed border-gray-400'
            }`}>
              <div className="text-sm font-semibold">
                {isOverUnassignZone ? 'Drop here to unassign' : 'Drop here to unassign guest'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <main className="flex-1 flex flex-col min-h-0">
        {/* Toolbar */}
        <div className="flex-shrink-0 border-b bg-white py-3">
          <DashboardContentContainer>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(`/event/${eventId}`)}>
                ← Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">Visual Table Planner (classic, Konva)</h1>
                <p className="text-sm text-muted-foreground">
                  {loading
                    ? 'Loading guest list…'
                    : `${assignedGuests.length} of ${allGuests.length} guests seated`}
                </p>
              </div>
              
              {/* Auto-save indicator */}
              {selectedRoomId && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  {autoSaving ? (
                    <>
                      <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                      Saving...
                    </>
                  ) : lastSaved ? (
                    <>
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      Saved {lastSaved.toLocaleTimeString()}
                    </>
                  ) : null}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {/* Room Dropdown */}
              <div className="flex items-center gap-2">
                <Select value={selectedRoomId || ''} onValueChange={handleRoomChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select Room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowCreateRoomModal(true)}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  Create Room
                </Button>
              </div>
              
              <Button variant="outline" size="sm" onClick={handleLoadTemplate}>
                <FolderOpen className="mr-2 h-4 w-4" />
                Load Template
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveAsTemplate}>
                <Upload className="mr-2 h-4 w-4" />
                Save Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAutoPopulateModal(true)}>
                <Users className="mr-2 h-4 w-4" />
                Auto Populate
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
              <Button variant="default" size="sm" onClick={handleExportPDFCustom}>
                <Download className="mr-2 h-4 w-4" />
                Designer
              </Button>
            </div>
          </div>
          </DashboardContentContainer>
        </div>

        {/* Main Content: paint toolbar + title immediately for LCP; defer heavy canvas until data is ready */}
        {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-muted-foreground">Loading table planner…</p>
          </div>
        </div>
        ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Canvas Area */}
          <div className="relative min-h-0 flex-1 bg-gray-100">
            {/* Canvas Controls */}
            <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
              <Button size="sm" onClick={() => handleAddTable('rectangular')}>
                <Plus className="mr-2 h-4 w-4" />
                Add Table
              </Button>
              <div className="flex flex-col gap-1 bg-white rounded-lg p-2 shadow-md">
                <Button variant="outline" size="sm" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleResetView}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <div className="text-xs text-center font-semibold py-1">
                  {Math.round(scale * 100)}%
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGrid(!showGrid)}
              >
                Grid: {showGrid ? 'On' : 'Off'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSnapToGrid(!snapToGrid)}
              >
                Edge Snap: {snapToGrid ? 'On' : 'Off'}
              </Button>
              
              {/* Duplicate button - only visible when at least one table is selected */}
              {selectedTableIds.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDuplicateTable}
                    className="bg-white"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </Button>
                  
                  {/* Table Configuration Panel - only show when exactly one table is selected */}
                  {selectedTableIds.size === 1 ? (
                  <div className="bg-white rounded-lg p-3 shadow-md space-y-3 max-w-xs">
                    <h3 className="font-semibold text-sm border-b pb-2">Table Settings</h3>
                    
                    {/* Table Name */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Name</label>
                      <Input
                        value={tables.find(t => selectedTableIds.has(t.id))?.name || ''}
                        onChange={(e) => {
                          setTables(tables.map(t => 
                            selectedTableIds.has(t.id) ? { ...t, name: e.target.value } : t
                          ));
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    
                    {/* Table Type */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Type</label>
                      <Select
                        value={tables.find(t => selectedTableIds.has(t.id))?.type || 'rectangular'}
                        onValueChange={(value) => {
                          const updatedTables = tables.map(t => {
                            if (selectedTableIds.has(t.id)) {
                              const updated = { ...t, type: value as TableConfig['type'] };
                              return calculateSeatingPositions(updated);
                            }
                            return t;
                          });
                          setTables(recalculateSeatNumbers(updatedTables));
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rectangular">Rectangular</SelectItem>
                          <SelectItem value="square">Square</SelectItem>
                          <SelectItem value="circular">Circular</SelectItem>
                          <SelectItem value="oval">Oval</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Seats */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Seats</label>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const table = tables.find(t => selectedTableIds.has(t.id));
                            if (table) {
                              const newSeats = Math.max(1, table.seats - 1);
                              const updatedTables = tables.map(t => {
                                if (selectedTableIds.has(t.id)) {
                                  const updated = { ...t, seats: newSeats };
                                  return calculateSeatingPositions(updated);
                                }
                                return t;
                              });
                              setTables(recalculateSeatNumbers(updatedTables));
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          -
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={tables.find(t => selectedTableIds.has(t.id))?.seats || 0}
                          onChange={(e) => {
                            const newSeats = parseInt(e.target.value) || 1;
                            const updatedTables = tables.map(t => {
                              if (selectedTableIds.has(t.id)) {
                                const updated = { ...t, seats: newSeats };
                                return calculateSeatingPositions(updated);
                              }
                              return t;
                            });
                            setTables(recalculateSeatNumbers(updatedTables));
                          }}
                          className="h-8 text-sm text-center flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const table = tables.find(t => selectedTableIds.has(t.id));
                            if (table) {
                              const newSeats = table.seats + 1;
                              const updatedTables = tables.map(t => {
                                if (selectedTableIds.has(t.id)) {
                                  const updated = { ...t, seats: newSeats };
                                  return calculateSeatingPositions(updated);
                                }
                                return t;
                              });
                              setTables(recalculateSeatNumbers(updatedTables));
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    
                    {/* Width & Height */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-700">Width (m)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="5.0"
                          value={((tables.find(t => selectedTableIds.has(t.id))?.width || 150) / 100).toFixed(1)}
                          onChange={(e) => {
                            const metres = parseFloat(e.target.value) || 1.0;
                            const updatedTables = tables.map(t => {
                              if (selectedTableIds.has(t.id)) {
                                const updated = { ...t, width: Math.round(metres * 100) };
                                return calculateSeatingPositions(updated);
                              }
                              return t;
                            });
                            setTables(updatedTables);
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-700">Height (m)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="5.0"
                          value={((tables.find(t => selectedTableIds.has(t.id))?.height || 150) / 100).toFixed(1)}
                          onChange={(e) => {
                            const metres = parseFloat(e.target.value) || 1.0;
                            const updatedTables = tables.map(t => {
                              if (selectedTableIds.has(t.id)) {
                                const updated = { ...t, height: Math.round(metres * 100) };
                                return calculateSeatingPositions(updated);
                              }
                              return t;
                            });
                            setTables(updatedTables);
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Rotation */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Rotation (°)</label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="360"
                          value={tables.find(t => selectedTableIds.has(t.id))?.rotation || 0}
                          onChange={(e) => {
                            setTables(tables.map(t => 
                              selectedTableIds.has(t.id) ? { ...t, rotation: parseInt(e.target.value) || 0 } : t
                            ));
                          }}
                          className="h-8 text-sm flex-1"
                        />
                      </div>
                      <div className="flex gap-1">
                        {[0, 90, 180, 270].map(angle => (
                          <Button
                            key={angle}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setTables(tables.map(t => 
                                selectedTableIds.has(t.id) ? { ...t, rotation: angle } : t
                              ));
                            }}
                            className="h-7 text-xs flex-1"
                          >
                            {angle}°
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Table Color */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Table Color</label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={tables.find(t => selectedTableIds.has(t.id))?.color || '#ffffff'}
                          onChange={(e) => {
                            setTables(tables.map(t => 
                              selectedTableIds.has(t.id) ? { ...t, color: e.target.value } : t
                            ));
                          }}
                          className="w-12 h-8 p-1 cursor-pointer"
                        />
                        <div className="flex gap-1 flex-wrap flex-1">
                          {['#ffffff', '#8B4513', '#2c3e50', '#e74c3c'].map(color => (
                            <button
                              key={color}
                              onClick={() => {
                                setTables(tables.map(t => 
                                  selectedTableIds.has(t.id) ? { ...t, color } : t
                                ));
                              }}
                              className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-400"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Chair Color */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Chair Color</label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={tables.find(t => selectedTableIds.has(t.id))?.chairColor || '#e5e7eb'}
                          onChange={(e) => {
                            setTables(tables.map(t => 
                              selectedTableIds.has(t.id) ? { ...t, chairColor: e.target.value } : t
                            ));
                          }}
                          className="w-12 h-8 p-1 cursor-pointer"
                        />
                        <div className="flex gap-1 flex-wrap flex-1">
                          {['#e5e7eb', '#10b981', '#3b82f6', '#ef4444'].map(color => (
                            <button
                              key={color}
                              onClick={() => {
                                setTables(tables.map(t => 
                                  selectedTableIds.has(t.id) ? { ...t, chairColor: color } : t
                                ));
                              }}
                              className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-400"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Seat Priority */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Seat Priority</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={tables.find(t => selectedTableIds.has(t.id))?.seatNumberPriority || 1}
                        onChange={(e) => {
                          const updatedTables = tables.map(t => 
                            selectedTableIds.has(t.id) ? { ...t, seatNumberPriority: parseInt(e.target.value) || 1 } : t
                          );
                          setTables(recalculateSeatNumbers(updatedTables));
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    
                    {/* Delete Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const table = tables.find(t => selectedTableIds.has(t.id));
                        if (table) {
                          confirmAction({
                            title: 'Delete Table',
                            text: `Are you sure you want to delete "${table.name}"?`,
                            icon: 'warning'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              const firstSelectedId = Array.from(selectedTableIds)[0];
                              if (firstSelectedId) {
                                handleDeleteTable(firstSelectedId);
                              }
                              showSuccess('Table deleted');
                            }
                          });
                        }
                      }}
                      className="w-full h-8 text-sm"
                    >
                      Delete Table
                    </Button>
                  </div>
                  ) : selectedTableIds.size > 1 ? (
                    <div className="bg-white rounded-lg p-3 shadow-md space-y-3 max-w-xs">
                      <h3 className="font-semibold text-sm border-b pb-2">Multiple Tables Selected</h3>
                      <p className="text-sm text-gray-600">
                        {selectedTableIds.size} tables selected. Drag to move them together.
                      </p>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Konva Stage */}
            <div 
              style={{ 
                width: stageWidth, 
                height: stageHeight, 
                overflow: 'hidden',
                cursor: isDraggingTable ? 'grabbing' : 'grab',
                position: 'relative'
              }}
              onDragOver={handleStageDragOver}
              onDrop={handleStageDrop}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onWheel={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Convert DOM wheel event to Konva wheel event
                const konvaEvent = {
                  evt: e,
                  target: stageRef.current,
                  type: 'wheel',
                  pointerId: 0,
                  currentTarget: stageRef.current,
                  cancelBubble: false
                } as unknown as Konva.KonvaEventObject<WheelEvent>;
                handleWheel(konvaEvent);
              }}
            >
              <Stage
                ref={stageRef}
                width={stageWidth}
                height={stageHeight}
                scaleX={scale}
                scaleY={scale}
                x={stagePos.x}
                y={stagePos.y}
                draggable={!isDraggingTable && !isDraggingSeat}
                onDragStart={(e) => {
                  if (isDraggingTable || isDraggingSeat) return;
                  if (e.target.getClassName() === 'Group') return;
                  isViewportPanningRef.current = true;
                  cancelPendingViewportAutoSave();
                }}
                onDragEnd={() => {
                  if (isDraggingTable || isDraggingSeat) return;
                  isViewportPanningRef.current = false;
                  const st = stageRef.current;
                  if (st) {
                    setStagePos({ x: st.x(), y: st.y() });
                  }
                  scheduleViewportAutoSave(500);
                }}
                onMouseMove={(e: Konva.KonvaEventObject<MouseEvent>) => {
                  // Check if dragging over unassign zone
                  if (isDraggingSeat || draggedGuest) {
                    const pointerPos = e.target.getStage()?.getPointerPosition();
                    if (pointerPos && stageRef.current) {
                      const container = stageRef.current.container();
                      const rect = container.getBoundingClientRect();
                      const globalY = rect.top + pointerPos.y;
                      
                      // Check if in unassign zone (below header, top 80px area)
                      // Header is typically around 64px, so zone starts at ~80px
                      updateUnassignZone(globalY >= 64 && globalY < 144);
                    }
                  }
                }}
                onMouseUp={(e: Konva.KonvaEventObject<MouseEvent>) => {
                  // Handle seat drop on mouse up
                  if (isDraggingSeat && draggedSeat && stageRef.current) {
                    const stage = stageRef.current;
                    const pointerPos = stage.getPointerPosition();
                    if (pointerPos) {
                      const container = stage.container();
                      const rect = container.getBoundingClientRect();
                      const globalY = rect.top + pointerPos.y;
                      
                      // Check if dropped in unassign zone (below header)
                      if (globalY >= 64 && globalY < 144) {
                        // Unassign guest from seat
                        const sourceTable = tables.find(t => t.id === draggedSeat.tableId);
                        if (sourceTable) {
                          const updatedTables = tables.map((table) => {
                            if (table.id === draggedSeat.tableId) {
                              return {
                                ...table,
                                seating: table.seating.map((seat) =>
                                  seat.seatNumber === draggedSeat.seatNumber
                                    ? { ...seat, rsvpId: null, role: null }
                                    : seat
                                ),
                              };
                            }
                            return table;
                          });
                          setTables(updatedTables);
                          
                          const guest = allGuests.find(g => g.id === draggedSeat.guestId);
                          if (guest) {
                            showSuccess(`${guest.name} unassigned from seat`);
                          }
                        }
                        setDraggedSeat(null);
                        setIsDraggingSeat(false);
                        setDragPreview(null);
                        setIsOverUnassignZone(false);
                        setHighlightedRelatedGuests(new Set());
                        return;
                      }
                      
                      // Convert to stage coordinates
                      const stageX = (pointerPos.x - stagePos.x) / scale;
                      const stageY = (pointerPos.y - stagePos.y) / scale;
                      
                      // Find which seat is at this position
                      const seatRadius = 24;
                      let foundSeat: { tableId: string; seatNumber: number } | null = null;
                      
                      for (const table of tables) {
                        const rotRad = (table.rotation * Math.PI) / 180;
                        const cos = Math.cos(-rotRad);
                        const sin = Math.sin(-rotRad);
                        
                        const dx = stageX - table.x;
                        const dy = stageY - table.y;
                        const localX = dx * cos - dy * sin;
                        const localY = dx * sin + dy * cos;
                        
                        for (const seat of table.seating) {
                          const seatX = seat.x;
                          const seatY = seat.y;
                          const distance = Math.sqrt(
                            Math.pow(localX - seatX, 2) + Math.pow(localY - seatY, 2)
                          );
                          
                          if (distance <= seatRadius) {
                            foundSeat = { tableId: table.id, seatNumber: seat.seatNumber };
                            break;
                          }
                        }
                        
                        if (foundSeat) break;
                      }
                      
                      if (foundSeat) {
                        handleSeatDrop(foundSeat.tableId, foundSeat.seatNumber);
                        resetSeatDragState();
                      } else {
                        resetSeatDragState();
                      }
                    }
                  }
                }}
              >
              <Layer>
                {/* Background */}
                <Rect
                  x={0}
                  y={0}
                  width={TABLE_PLANNER_PLAYABLE_AREA_SIZE}
                  height={TABLE_PLANNER_PLAYABLE_AREA_SIZE}
                  fill="#f3f4f6"
                  onClick={() => setSelectedTableIds(new Set())}
                  onTap={() => setSelectedTableIds(new Set())}
                />

                {/* Grid */}
                {gridLayer}


                {/* Tables */}
                {tables.map((table) => (
                  <VisualTable
                    key={table.id}
                    tableId={table.id}
                    table={table}
                    isSelected={selectedTableIds.has(table.id)}
                    highlightedRelatedGuests={highlightedRelatedGuests}
                    useLocalDragPosition={selectedTableIds.size <= 1}
                    onSelect={handleTableSelect}
                    onDragStart={handleTableDragStart}
                    onDragMove={handleTableDragMove}
                    onDragEnd={handleTableDragEnd}
                    onDoubleClick={handleTableDoubleClick}
                    onSeatClick={handleSeatClick}
                    onSeatDrop={handleSeatDrop}
                    onSeatDragStart={handleSeatDragStart}
                    onResize={handleTableResize}
                    onSeatsChange={handleSeatsChange}
                    onRotate={handleTableRotate}
                    onToggleSeatSide={handleToggleSeatSide}
                    onAdjustSide={adjustSideSeatCount}
                    getGuestName={getGuestName}
                    scale={scale}
                    onTableGroupRef={registerTableGroup}
                    reduceDropShadow={isDraggingTable}
                  />
                ))}
              </Layer>
            </Stage>
            
            {/* Drag Preview - shows seat being dragged */}
            {dragPreview && (
              <div
                style={{
                  position: 'fixed',
                  left: dragPreview.x - 24,
                  top: dragPreview.y - 24,
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  border: '2px solid #059669',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  pointerEvents: 'none',
                  zIndex: 10000,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                {(() => {
                  const nameParts = dragPreview.guestName.split(' ');
                  const firstName = nameParts[0] || '';
                  const lastName = nameParts.slice(1).join(' ') || '';
                  return (
                    <>
                      {firstName && (
                        <div style={{
                          color: '#000000',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          lineHeight: '10px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '44px',
                          whiteSpace: 'nowrap',
                        }}>
                          {firstName}
                        </div>
                      )}
                      {lastName && (
                        <div style={{
                          color: '#000000',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          lineHeight: '10px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '44px',
                          whiteSpace: 'nowrap',
                        }}>
                          {lastName}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            </div>
          </div>

          {/* Sidebar */}
          {showSidebar && (
            <div className="flex w-96 flex-shrink-0 flex-col border-l bg-white min-h-0">
              <div className="flex flex-1 flex-col min-h-0 p-4">
                {/* Guest List */}
                <Card className="flex min-h-0 flex-1 flex-col">
                  <CardHeader className="flex-shrink-0 pb-3">
                    <CardTitle className="text-sm">Guest List</CardTitle>
                    <CardDescription>
                      {availableGuests.length} unassigned, {assignedGuests.length} seated
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex min-h-0 flex-1 flex-col p-0">
                    <div
                      ref={guestListScrollRef}
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-2"
                    >
                      {allGuests.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          No guests found
                        </div>
                      ) : (
                        <div
                          className="relative w-full"
                          style={{ height: `${guestRowVirtualizer.getTotalSize()}px` }}
                        >
                          {guestRowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const guest = sortedGuests[virtualRow.index];
                            const tableInfo = guest.id ? guestSeatAssignments.get(guest.id) ?? null : null;

                            return (
                              <div
                                key={guest.id}
                                data-index={virtualRow.index}
                                ref={guestRowVirtualizer.measureElement}
                                draggable
                                onDragStart={(e) => handleGuestDragStart(guest, e)}
                                onDragEnd={handleGuestDragEnd}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  transform: `translateY(${virtualRow.start}px)`,
                                }}
                                className={`
                                  flex items-center justify-between rounded-lg border p-2 cursor-move
                                  hover:bg-muted/50 transition-colors
                                  ${draggedGuest?.id === guest.id ? 'opacity-50' : ''}
                                  ${tableInfo ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}
                                `}
                              >
                                <div className="min-w-0 flex-1 truncate text-sm font-medium">
                                  {formatGuestListDisplayName(guest, ranks)}
                                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                                    {tableInfo
                                      ? `${tableInfo.tableName} • Seat ${tableInfo.seatNumber}`
                                      : 'Unassigned'}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
        )}
      </main>


      {/* Modals */}
      {configModalTable && (
        <TableConfigModal
          table={configModalTable}
          onClose={() => setConfigModalTable(null)}
          onSave={handleSaveTableConfig}
          onUpdate={handleUpdateTableConfig}
          onDelete={handleDeleteTable}
        />
      )}

      {guestAssignmentModal && (
        <GuestAssignmentModal
          tableId={guestAssignmentModal.tableId}
          tableName={guestAssignmentModal.tableName}
          seatNumber={guestAssignmentModal.seatNumber}
          currentGuest={guestAssignmentModal.currentGuest}
          allGuests={allGuests}
          availableGuests={availableGuests}
          onClose={() => setGuestAssignmentModal(null)}
          onAssign={handleAssignGuest}
          onUnassign={handleUnassignGuest}
        />
      )}

      {showPDFExportModal && (
        <PDFExportModal
          onClose={() => setShowPDFExportModal(false)}
          guests={allGuests}
          tables={tables}
          allRoomTables={allRoomTables}
          rooms={rooms}
          eventId={eventId}
          unitId={unitId}
          onFloorPlansChanged={fetchRooms}
        />
      )}

      {showAutoPopulateModal && (
        <AutoPopulateModal
          isOpen={showAutoPopulateModal}
          onClose={() => setShowAutoPopulateModal(false)}
          onAutoPopulateAll={() => autoPopulateGuests(true)}
          onAutoPopulateUnassigned={() => autoPopulateGuests(false)}
        />
      )}

      {/* Create Room Modal */}
      {showCreateRoomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">
              {rooms.length === 0 ? 'Create Your First Room' : 'Create New Room'}
            </h2>
            {rooms.length === 0 && (
              <p className="text-sm text-gray-600 mb-4">
                To get started with the table planner, you need to create at least one room.
              </p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room Name
                </label>
                <Input
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="e.g., Main Hall, Dining Room..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      createRoom();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                {rooms.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateRoomModal(false);
                      setNewRoomName('');
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={createRoom}
                  disabled={!newRoomName.trim()}
                >
                  Create Room
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to calculate seating positions
function calculateSeatingPositions(config: TableConfig): TableConfig {
  const { type, width, height, seats, seating, seatPlacement } = config as any;
  const seatCounts: any = (config as any).seatCounts || null;
  
  // Ensure seating array has the correct number of slots (no extra for end seats)
  const updatedSeating = [...seating];
  while (updatedSeating.length < seats) {
    updatedSeating.push({
      seatNumber: updatedSeating.length + 1,
      rsvpId: null,
      role: null,
      tag: null,
      x: 0,
      y: 0,
    });
  }
  // Trim to exact seat count
  updatedSeating.splice(seats);
  
  const chairOffset = 30;

  switch (type) {
    case 'circular':
    case 'oval': {
      const radiusX = width / 2;
      const radiusY = type === 'oval' ? height / 2 : width / 2;
      const angleStep = (2 * Math.PI) / seats;

      for (let i = 0; i < seats && i < updatedSeating.length; i++) {
        const angle = angleStep * i - Math.PI / 2;
        updatedSeating[i] = {
          ...updatedSeating[i],
          x: Math.cos(angle) * (radiusX + chairOffset),
          y: Math.sin(angle) * (radiusY + chairOffset),
        };
      }
      break;
    }

    case 'rectangular': {
      // Rectangular: prefer explicit seatCounts if present
      if (seatPlacement?.noSeats) {
        for (let i = 0; i < updatedSeating.length; i++) {
          updatedSeating[i] = { ...updatedSeating[i], x: 0, y: 0 };
        }
        break;
      }

      let seatIndex = 0;

      // End seats (use counts if provided, else boolean flags)
      const leftEndCount = seatCounts?.leftEnd ?? (seatPlacement?.leftEnd ? 1 : 0);
      const rightEndCount = seatCounts?.rightEnd ?? (seatPlacement?.rightEnd ? 1 : 0);
      // Distribute end chairs vertically along the table ends
      if (leftEndCount > 0) {
        const spacing = height / (leftEndCount + 1);
        for (let i = 0; i < leftEndCount && seatIndex < seats; i++, seatIndex++) {
          const yPos = spacing * (i + 1) - height / 2;
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: -width / 2 - chairOffset, y: yPos };
        }
      }
      if (rightEndCount > 0) {
        const spacing = height / (rightEndCount + 1);
        for (let i = 0; i < rightEndCount && seatIndex < seats; i++, seatIndex++) {
          const yPos = spacing * (i + 1) - height / 2;
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: width / 2 + chairOffset, y: yPos };
        }
      }

      // Side seats based on counts or flags
      const remaining = seats - seatIndex;
      const topCount = seatCounts?.top ?? (seatPlacement?.topSide ?? (seatPlacement?.bothSides ?? true) ? Math.ceil(remaining / 2) : 0);
      const bottomCount = seatCounts?.bottom ?? (seatPlacement?.bottomSide ?? (seatPlacement?.bothSides ?? true) ? remaining - (seatCounts?.top ?? Math.ceil(remaining / 2)) : 0);

      if (topCount > 0) {
        const topSpacing = width / (topCount + 1);
        for (let i = 0; i < topCount && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: topSpacing * (i + 1) - width / 2, y: -height / 2 - chairOffset };
        }
      }
      if (bottomCount > 0) {
        const bottomSpacing = width / (bottomCount + 1);
        for (let i = 0; i < bottomCount && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: bottomSpacing * (i + 1) - width / 2, y: height / 2 + chairOffset };
        }
      }
      break;
    }

    case 'square': {
      // Handle seat placement options for square tables
      if (seatPlacement?.noSeats) {
        // No seats - clear all seating positions
        for (let i = 0; i < updatedSeating.length; i++) {
          updatedSeating[i] = {
            ...updatedSeating[i],
            x: 0,
            y: 0,
          };
        }
        break;
      }

      // Prefer seatCounts
      const resolvedTop = seatCounts?.top ?? (seatPlacement?.topSide ?? true ? Math.ceil(seats / 4) : 0);
      const resolvedBottom = seatCounts?.bottom ?? (seatPlacement?.bottomSide ?? true ? Math.ceil(seats / 4) : 0);
      const resolvedLeft = seatCounts?.left ?? (seatPlacement?.leftSide ?? true ? Math.ceil(seats / 4) : 0);
      const resolvedRight = seatCounts?.right ?? (seatPlacement?.rightSide ?? true ? seats - resolvedTop - resolvedBottom - resolvedLeft : 0);
      let seatIndex = 0;
      if (resolvedTop > 0) {
        const spacing = width / (resolvedTop + 1);
        for (let i = 0; i < resolvedTop && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: spacing * (i + 1) - width / 2, y: -height / 2 - chairOffset };
        }
      }
      if (resolvedRight > 0) {
        const spacing = height / (resolvedRight + 1);
        for (let i = 0; i < resolvedRight && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: width / 2 + chairOffset, y: spacing * (i + 1) - height / 2 };
        }
      }
      if (resolvedBottom > 0) {
        const spacing = width / (resolvedBottom + 1);
        for (let i = 0; i < resolvedBottom && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: width / 2 - spacing * (i + 1), y: height / 2 + chairOffset };
        }
      }
      if (resolvedLeft > 0) {
        const spacing = height / (resolvedLeft + 1);
        for (let i = 0; i < resolvedLeft && seatIndex < seats; i++, seatIndex++) {
          updatedSeating[seatIndex] = { ...updatedSeating[seatIndex], x: -width / 2 - chairOffset, y: height / 2 - spacing * (i + 1) };
        }
      }
      
      break;
    }
  }

  return { ...config, seating: updatedSeating };
}

